import zipState from "zip-state";

import {
  MHMDA_STATES,
  NOOM_MED_ADDON_SUBSCRIPTION_PRODUCT_NAME,
  routeConstants,
  SessionStorageKey,
} from "@utils/constants";
import { StatesLabsNotIncluded } from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/insuranceConstants";
import {
  belowMinimumNormalBMI,
  calculateBMI,
  getCourseDuration,
} from "@utils/calculateGoals";
import { planDurationMonthsToTelehealthNoomPlanId } from "@utils/plansMapping";
import {
  DE_COURSE_PACK_ELIGIBLE_COUNTRIES,
  EN_COURSE_PACK_ELIGIBLE_COUNTRIES,
} from "@utils/userSegment/constants";
import { queryClient } from "@utils/fetch";
import { getMeristemContext, getSubdivision } from "@utils/meristemContext";

import { appConfig } from "src/config";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import {
  FeatureFlags,
  getFeatureFlagValue,
} from "src/pageDefinitions/featureFlags";
import { getPageSet } from "src/pageDefinitions/pageSets";
import { TELEHEALTH_FREE_PLAN_ID } from "src/funnels/payment-en/pages/telehealthRedirects";
import {
  HEALTHY_WEIGHT_CURRICULUM_PREFIX,
  hrtPaymentCheckoutLayer,
  hrtPaymentResultsLayer,
  hrtSurveyLayer,
  noomMedOfferLayer,
  noomPremiumFitonSurveyLayer,
  noomPremiumSurveyLayer,
  noomSummitSurveyLayer,
  noomWeightMedBranchingLayer,
} from "src/pageDefinitions/goto/constants";
import {
  selectAMCoursepack,
  selectGlp1Coursepack,
  selectMovementCoursepack,
  selectMWCoursepack,
  selectSleepCoursepack,
} from "src/utils/redux/slices/paymentAddon";
import {
  canUseMobileSharingInterface,
  canUseNavigatorShare,
} from "src/hooks/sharingApi";
import { isHolidayPromotion } from "src/utils/holidayPromo";

import getStore, { CoreReduxState } from "../redux/store";
import {
  isEmailWinback,
  isFromEmail,
  isFromNoomWeightEmail,
  isUSEmail,
} from "./email";
import { checkSegments, createOneTimeConditionSegment } from "./util";
import { canRetrieveWeight } from "../refactoredSurvey";
import { loadWinbackInfo } from "../redux/slices/winbackInfo";
import {
  isAndroidPlatform,
  isAppAndroid,
  isAppAndroidWinback,
  isAppIos,
  isAppIosBase,
  isAppIosBaseAndWinback,
  isAppIOSUpsell,
  isAppUpsell,
  isAppVersionGTE,
  isFreemiumUnidentifiedRoute,
  isIOSPlatform,
} from "./in-app";
import { isMealPlan, isMealWorkoutBundle, isWorkoutPlan } from "../addons";
import { isInExperimentConflictingWithSummitCoursePack } from "../api/addons/addons";
import { getIOSMessageHandlers } from "../in-app/messageHandlers";
import {
  getPromoCodeState,
  isPromoCodePersisted,
} from "../redux/slices/promoCode";
import { loadActiveSubscriptionDataState } from "../redux/slices/currentSubscriptionData";
import { getMeristemConfigValue } from "../meristemConfig";
import { isMultiUserPlan } from "../multiUserPlan";
import {
  GET_PEERS_CACHE_KEY,
  loadPeers,
  MultiUserPlanPeerResponse,
} from "../api/noomTogether";
import { paymentAddonProducts } from "../paymentAddon";
import { isCheckoutOfferTimerExpired } from "../checkoutTimer";
import { isPersonalizedPlanTimerExpired } from "../personalizedPlanTimer";
import { getURLParams } from "../urlParams";
import { isDevModEnabled } from "../runtimeFlags";
import { hasFallbackPromotionalOfferConfiguration } from "../fallbackPromotionalOffer";
import { getCampaignFromStorage } from "../api/campaign";
import { freeMonthsOfferConfiguration } from "../freeMonthsOffer";
import { getSessionState } from "src/pageDefinitions/session";
import { calculateAge } from "../datetime";
import { ServerContextState } from "../redux/slices/serverContext";
import { isEmail } from "../validate";
import {
  IDEAL_WEIGHT_VIBE_OFFER_ID,
  shouldSuppressVibeOffer,
  STEP_TRACKING_VIBE_OFFER_ID,
} from "../noomVibe";
import {
  isAdWordsSEM,
  isAffiliate,
  isAffiliateWithoutTrial,
  isAffiliateWithTrial,
  isAppAndroidBase,
  isAppAU,
  isAppBaseRoute,
  isAppCA,
  isAppEmail,
  isAppFreeOnboardingRoute,
  isAppUS,
  isAU,
  isB2BUpsellClinicalRoute,
  isBaseline,
  isCA,
  isCalorieClinicalRoute,
  isClinicalRoute,
  isCoachReferralsRoute,
  isCore,
  isCoreDirect,
  isCorePaid,
  isCorePaidWithoutAddWordsSEM,
  isCountry,
  isDeEmailMain,
  isDeLanguage,
  isDPP,
  isEmailMain,
  isEnLanguage,
  isEnMantle,
  isEnProgramLocale,
  isEsEmailMain,
  isEsLanguage,
  isEU,
  isFemaleSex,
  isFreemiumBranchedRoute,
  isFreemiumIdentifiedRoute,
  isFreemiumQuizRoute,
  isFreemiumUpsellRoute,
  isFreemiumWeightRoute,
  isHealthRoute,
  isHM,
  isHRTRoute,
  isInApp,
  isInAppEmployerSurvey,
  isInAppWinback,
  isIntl,
  isIntlMantle,
  isLegacyAffiliateTrialRoute,
  isMaleGender,
  isMantle,
  isMobile,
  isNoomVibeCodeRetrievalRoute,
  isNZ,
  isOrdersPurchaseCheckout,
  isPreSignup,
  isReferralsBuyflow,
  isReferralsRoute,
  isStrictCore,
  isSubscriptionInvalid,
  isUK,
  isUpsellClinicalRoute,
  isUpsellRoute,
  isUS,
  isUSInAppWinback,
  isUSWinback,
  isWebClinicalRoute,
  isWebWinback,
  isWinback,
} from ".";
import {
  isEligibleFor21DayTrial,
  isEligibleFor30DayTrial,
  isMedTierPlanId,
  isPremiumTierPlanId,
} from "@utils/plans";
import { hasAnsweredToGLP1FollowUpInCurrentSession } from "../glp1";
import { loadMedEntitlements } from "../redux/slices/medEntitlements";
import {
  getLoadedSubscriptionStatus,
  loadSubscriptionStatus,
} from "src/hooks/subscriptionStatus";
import {
  ELIGIBLE_SKUS_FOR_SWITCH,
  getEligibleMedProducts,
  isFulldoseGlp1Eligible,
  MED_FLOW,
  MED_SKU,
  passesMinBMIForMedSwitchingBuyflow,
} from "../medEligibility";
import { getInAppAutoCookie } from "../authCookieParser";
import visitTracker from "@noom/visit-tracker";
import { getCoachReferralInfo } from "../coachReferrals";
import {
  getTopSymptomsBasedOnUserSelected,
  MenopauseSymptoms,
} from "@components/hrt/utils/menopauseSymptoms";
import { getNoomSessionStorage } from "@utils/noomSessionStorage";
import { TelehealthIntakeTheme } from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/telehealthIntakeTheme";
import { PageSet } from "src/pageDefinitions/types";
import {
  getLiraglutideRollout,
  getLongevityMicrodoseRollout,
} from "../rollout";
import { isGlobalExitEnabled } from "../globalExit";
import { getLandingPageBMIs } from "src/utils/landingPageBMIs";
import { transformAgeYearsToAgeRange } from "@components/building/usePeopleHelpedValues";
import { isInsidePartnerWebView } from "src/pageDefinitions/goto/utils";
import { HealthReportUpsellContent, UpsellAdLocation } from "gql/graphql";
import { getActiveUser } from "@noom/hive-auth";
import { NON_MED_PLAN } from "../health/planRecommendation";

const HW_NON_VIP_BATCH_ID_DEV = 1;
const HW_NON_VIP_BATCH_ID_PROD = 354;
export const DPP_OVERRIDE_PLAN = "dpp-12-month";
export const USD_MONTHLY_PLAN_ID = "option_d_20221201_Ex1084var1email";
export const USD_MONTHLY_PLAN_ID_7_DAY_TRIAL = "option_d_20221222_Ex1084var1";
export const BANDIT_LOCAL_STORAGE_KEY = "mab_key";

export function isDevMode() {
  return isDevModEnabled();
}

export function isPrerender() {
  // eslint-disable-next-line no-underscore-dangle
  return !!(window as any).__prerender;
}

export function evalResultWithFeatureFlag(
  result: boolean,
  flag: keyof FeatureFlags
) {
  const flagValue = getFeatureFlagValue(flag) as boolean;
  if (flagValue === undefined) {
    return result;
  }
  return flagValue;
}

export const shouldEnrollUser = (state?: CoreReduxState) => {
  return !isInApp(state) && !isAppEmail(state) && !isUpsellRoute(state);
};

export const isHMEligible = () => isUS() && isEnLanguage();

export function shouldSuppressIdealWeightVibeOffer() {
  return shouldSuppressVibeOffer(IDEAL_WEIGHT_VIBE_OFFER_ID);
}

export const isIdealWeightVibeOfferEligible = () =>
  isEnLanguage() && !isFreemiumUpsellRoute();

export const shouldShowIdealWeightVibeOffer = () =>
  isIdealWeightVibeOfferEligible() &&
  !shouldSuppressIdealWeightVibeOffer() &&
  !isFreemiumUnidentifiedRoute() &&
  !isHealthRoute();

const hadPreviousITunesSubscription = () => {
  const urlParams = new URLSearchParams(window.location.search);
  // This parameter is set by the growth backend in the internal app endpoint
  // More details about that here: https://github.com/noom/eng-handbook/blob/master/teams/growth/product/Winbacks.md#more-about-in-app-routes
  return urlParams.get("had_itunes_sub") === "true";
};

const disableLPSurveySegment = checkSegments({
  featureFlagOverride: "disableLPSurvey",
});

export const isSurveyOnLPEligible = checkSegments({
  oneOf: [
    routeConstants.exsf01d,
    routeConstants.exsf01,
    routeConstants.affiliateTrialNPB,
    routeConstants.appEmail,
  ],
  noneOf: [isHM, isInApp, disableLPSurveySegment],
});

export const isBraintreeStripeiOSEligible = checkSegments({
  allOf: [hadPreviousITunesSubscription, routeConstants.app14DayReturning],
});

export const isGooglePlayPlansEligible = () => {
  // TODO (Brandon): enable when in-app is ready for release
  // const hasGooglePlayMessageHandler =
  //   !!getAndroidMessageHandlers()?.buyflowPurchaseProduct ||
  //   ["staging", "development", "local"].includes(appConfig.NOOM_ENV);

  // return isAppAndroidBase() && hasGooglePlayMessageHandler;
  return false;
};

export const isITunesPlansEligible = () => {
  const hasITunesMessageHandler =
    !!getIOSMessageHandlers()?.buyflowPurchaseProduct ||
    ["staging", "development", "local"].includes(appConfig.NOOM_ENV);

  if (isBraintreeStripeiOSEligible()) {
    return false;
  }

  return (
    isAppIos() && hasITunesMessageHandler && !isApplePurchaseLinkoutEnabled()
  );
};

export const isITunes1MonthDefaultPlanEligible = () => {
  const hasITunesDefaultPlanMessageHandler =
    !!getIOSMessageHandlers()?.buyflowPurchaseNoDetails ||
    ["staging", "development", "local"].includes(appConfig.NOOM_ENV);

  if (isBraintreeStripeiOSEligible()) {
    return false;
  }

  return isAppIos() && hasITunesDefaultPlanMessageHandler;
};

/**
 * Checks if the user can use the native app in-app purchase flow.
 */
export const isInAppNativePurchaseEligible = checkSegments({
  oneOf: [
    isITunes1MonthDefaultPlanEligible,
    isITunesPlansEligible,
    isGooglePlayPlansEligible,
  ],
  noneOf: [acceptedNoomClinicalOffer],
});

/**
 * Checks if the user can use the web-based in-app purchase flow.
 */
export const isInAppWebPurchaseEligible = checkSegments({
  oneOf: [isAppAndroid, isBraintreeStripeiOSEligible, isUpsellClinicalRoute],
});

const ffwdEligibleCurrencies = ["USD", "EUR", "GBP", "CAD", "AUD"];
export const isCurrencyFastForwardEligible = () => {
  const { currency = "USD" } = getStore().getState().serverContext;
  return ffwdEligibleCurrencies.includes(currency);
};

const isUSOnlyFastForwardEligibleRoute = checkSegments({
  allOf: [isUS],
  oneOf: [isEmailMain, isEmailWinback, isReferralsRoute],
});

export const hasPromoCodeIntl = checkSegments({
  allOf: [hasPromoCode, isIntl],
});

export const isFastForwardEligible = checkSegments({
  allOf: [isEnLanguage, isUS],
  noneOf: [isTelehealthPlan, userHasPurchasedNoomClinical],
  oneOf: [
    isCore,
    routeConstants.winback,
    isAppAndroidBase,
    isAffiliateWithTrial,
    isUSOnlyFastForwardEligibleRoute,
  ],
});

isFastForwardEligible.load = async () => {
  await getStore().dispatch(loadActiveSubscriptionDataState({}));
};

const isNoomPremiumEligibleUS = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [
    isMantle,
    isHealthRoute,
    isWebWinback,
    routeConstants.emailMain,
    routeConstants.app14DayFtWeb,
  ],
  noneOf: [isCoachReferralsRoute],
});

export const isAffiliateWithLongTrial = () => {
  return isEligibleFor30DayTrial() || isEligibleFor21DayTrial();
};

export const isPremiumTextCoachingSubscriptionEligible = checkSegments({
  allOf: [isUS, isEnProgramLocale, isEnLanguage],
  noneOf: [userHasPurchasedNoomClinical],
});

export const isNoomPremiumSubscriptionEligible = checkSegments({
  allOf: [isEnLanguage],
});

export const isFreeSupporterPlanEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [isCore, isWebWinback, isEmailMain],
});

const isLanguageNoomPremiumEligible = checkSegments({
  oneOf: [isEnLanguage, isDeLanguage, isEsLanguage],
});

const isNoomPremiumEligibleIntl = checkSegments({
  allOf: [isIntl, isLanguageNoomPremiumEligible],
  oneOf: [
    isMantle,
    isWebWinback,
    routeConstants.emailMain,
    routeConstants.freeTrial,
  ],
  noneOf: [isCoachReferralsRoute],
});

export const isInNoomPremiumSurvey = checkSegments({
  allOf: [
    ({ linearBuyflow }) =>
      getPageSet(linearBuyflow.pageSetName)?.layer === noomPremiumSurveyLayer ||
      getPageSet(linearBuyflow.pageSetName)?.layer ===
        noomPremiumFitonSurveyLayer,
  ],
});

export const isAddonStoreEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [isAppIosBase, isTelehealthPlan, userHasPurchasedNoomClinical],
  oneOf: [
    routeConstants.direct,
    routeConstants.exsf01d,
    routeConstants.exsf01,
    isAffiliateWithoutTrial,
    routeConstants.emailMain,
    isReferralsBuyflow,
    routeConstants.freeTrial,
    isWebWinback,
    isAppAndroidBase,
    routeConstants.affiliateTrialNPB,
    routeConstants.appEmail,
    routeConstants.app14DayFtWeb,
    isCoachReferralsRoute,
  ],
});

export const isBodyScanOnWeb = checkSegments({
  allOf: [isAppEmail],
});

export const isWelcomeToNoomEligible = checkSegments({
  oneOf: [isMobile, isMantle, isFromEmail, isWinback, isInAppWinback],
});

export const userHasPurchasedNoomPremium = checkSegments({
  allOf: [(state) => state.addOns.purchasedNoomPremium],
});

export const userHasPurchasedNoomPremiumWithTextCoaching = checkSegments({
  allOf: [(state) => state.addOns.purchasedNoomPremiumWithTextCoaching],
});

export const userHasPurchasedNoomPremiumSubscription = checkSegments({
  allOf: [(state) => state.addOns.purchasedNoomPremiumSubscription],
});

export const isNoomPremiumConsultationSurveyEligible = checkSegments({
  oneOf: [userHasPurchasedNoomPremium, hasBoughtMealAndWorkoutPlan],
});

export const navigateToNoomPremiumConsultationSurvey = checkSegments({
  allOf: [userHasPurchasedNoomPremium],
  oneOf: [
    isMantle,
    routeConstants.affiliateTrialNPB,
    isReferralsBuyflow,
    isEmailWinback,
    isWinback,
    isAppAndroidBase,
  ],
  noneOf: [userHasPurchasedNoomPremiumSubscription],
});

export const shouldNavigateToNoomPremiumFitonConsultationSurvey = checkSegments(
  {
    allOf: [
      userHasPurchasedNoomPremium,
      userHasPurchasedNoomPremiumSubscription,
    ],
    oneOf: [
      isMantle,
      routeConstants.affiliateTrialNPB,
      isReferralsBuyflow,
      isEmailWinback,
      isWinback,
      isAppAndroidBase,
    ],
  }
);

export const isImportantDateEligible = checkSegments({
  allOf: [isEnLanguage],
  oneOf: [isMantle],
});

export const isFivePercentEligible = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle],
});

/**
 * Implements the logic for targeting users for dynamic plan graph(ex417)
 */
export const isDynamicPlanGraphEligible = checkSegments({
  oneOf: [
    isMantle,
    isWinback,
    isUSEmail,
    isDeEmailMain,
    isInAppWinback,
    isUSInAppWinback,
  ],
  allOf: [canRetrieveWeight],
});

export const isFinalGraphEligible = checkSegments({
  oneOf: [isMantle, isUSWinback, isUSEmail, isUSInAppWinback],
});

export const isShowingPaymentGraphExtraContent = checkSegments({
  oneOf: [isMantle, isUSWinback, isInAppWinback],
});

/**
 * Verifies if the current user should see the final update graph
 */
export const shouldShowFinalUpdateGraph = (state = getStore().getState()) => {
  const surveyAnswers = getSurveyAnswers(state);
  const { weight, idealWeight, weightKg, idealWeightKg } = surveyAnswers;
  const courseDuration = getCourseDuration(surveyAnswers);

  // EnUSBaseline + App14DayBase routes
  if (
    weight &&
    idealWeight &&
    weightKg &&
    idealWeightKg &&
    isFivePercentEligible() &&
    courseDuration <= 8
  ) {
    return true;
  }

  return shouldCondenseOrShowFinalUpdateGraph(courseDuration);
};

function shouldCondenseOrShowFinalUpdateGraph(courseDuration: number) {
  const isCourseDurationEligible =
    courseDuration >= 2 &&
    ((courseDuration <= 8 && isIntlMantle()) || courseDuration <= 6);
  return (
    canRetrieveWeight() && isFinalGraphEligible() && isCourseDurationEligible
  );
}

export const hasNoTrial = checkSegments({
  oneOf: [
    routeConstants.affiliateNoTrial,
    routeConstants.affiliateNoTrialPromo,
    routeConstants.ndpp,
    isGooglePlayPlansEligible,
  ],
});

export const isDirectTraffic = checkSegments({
  oneOf: [
    routeConstants.direct,
    routeConstants.appUpsell,
    routeConstants.hmDirect,
  ],
});

export const isTrialFeeWaived = checkSegments({
  oneOf: [
    isInAppWinback,
    isAppIosBase,
    isAppIOSUpsell,
    isReferralsBuyflow,
    routeConstants.freeTrial,
    routeConstants.hmReferrals,
    routeConstants.affiliate,
    routeConstants.phillips,
    routeConstants.humana,
  ],
});

export const isChooseTrialPriceNewStyleEligible = checkSegments({
  oneOf: [isMantle, isEmailMain, isHM, isInApp],
});

export const isTrialEligible = (
  { recommendedPlan } = getStore().getState()
) => {
  return recommendedPlan.has_trial && !hasNoTrial();
};

export function showChooseTrialPage(state = getStore().getState()) {
  if (isApplePurchaseLinkoutEnabled()) {
    return false;
  }
  if (isEnLanguage()) {
    return (
      !isTrialFeeWaived() &&
      !hasNoTrial() &&
      // Note (Vitza): As the content of this file is quite logically put
      // together by domains, it would need a considerable reorder to have this
      // method be defined above this line.
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      !showMedCheckout() &&
      !isEligibleForCoachReferralBuyflow()
    );
  }
  return (
    isCoreDirect(state) ||
    isCorePaid(state) ||
    isDeEmailMain() ||
    isEsEmailMain() ||
    isHM()
  );
}

export const isPromoCodeBoxEnabled = checkSegments({
  noneOf: [
    isInApp,
    isApplePurchaseLinkoutEnabled,
    isCorePaidWithoutAddWordsSEM,
    isWinback,
    routeConstants.affiliateNoTrial,
  ],
  featureFlagOverride: "togglePromoCode",
});

export const shouldValidateTokenFlag = checkSegments({
  oneOf: [routeConstants.appUpsell],
});

export const shouldShowOnscreenBackButtons = checkSegments({
  oneOf: [isInApp],
});

export const isPaidTraffic = checkSegments({
  oneOf: [
    isCorePaid,
    routeConstants.experiment,
    routeConstants.affiliateTrialNPB,
    routeConstants.hmPaid,
    routeConstants.hmExsf01,
    routeConstants.hmExsf01d,
    routeConstants.hmAwsem,
    routeConstants.winback,
    routeConstants.hmWinback,
  ],
});

export const shouldShowExplainerLoadingPage = checkSegments({
  oneOf: [isMantle, isWinback],
});

export const shouldUseMobileOptimizedControls = checkSegments({
  oneOf: [isMantle, isWinback, routeConstants.hmPaid, routeConstants.hmDirect],
});

const searchActiveRenewingOrPendingAddonSubscription = (
  serverContext: ServerContextState,
  expectedProductType?: string
) => {
  return (serverContext?.addon_subscriptions || []).find(
    (addOnSubscription) => {
      return (
        addOnSubscription &&
        (!expectedProductType ||
          addOnSubscription.product === expectedProductType) &&
        ((addOnSubscription.status === "ACTIVE" &&
          addOnSubscription.auto_renewal_enabled) ||
          addOnSubscription.status === "PENDING")
      );
    }
  );
};

export const hasActiveMedAddOnSubscription = () => {
  return searchActiveRenewingOrPendingAddonSubscription(
    getStore().getState().serverContext,
    NOOM_MED_ADDON_SUBSCRIPTION_PRODUCT_NAME
  );
};

export const hasActiveAddOnSubscription = (
  serverContext: ServerContextState
) => {
  return searchActiveRenewingOrPendingAddonSubscription(serverContext);
};

export const shouldShowCounterOfferAddonDisclaimerInNoomWeightCounterOffer = (
  serverContext: ServerContextState
) => {
  return isEnLanguage() && isUS() && hasActiveAddOnSubscription(serverContext);
};

export const isEligibleForNX1CourseEnhancements = checkSegments({
  allOf: [isEnLanguage, shouldShowCoursePackCheckoutOffers],
  noneOf: [isUK],
});

export const isEligibleForFreeAgingMetabolismOffer = checkSegments({
  allOf: [isEligibleForNX1CourseEnhancements, isIntl],
});

export const isEligibleForFreemiumWeightBuyflow = checkSegments({
  oneOf: [isFreemiumWeightRoute, isFreemiumBranchedRoute],
});

export const isBMINoomClinicalEligible = () => {
  return (
    getEligibleMedProducts({
      checkBMI: true,
      checkHealthRisks: false,
      checkState: false,
      checkFlow: true,
      requirePreviousMedicationsAnswer: true,
      requireHighestBMIAnswer: true,
      disabledNamedChecks: [
        "isQualifiedForPD",
        "isNotEligibleForCompoundedSemaglutide",
      ],
    }).length > 0
  );
};

export const isBMINoomClinicalEligibleBeforePrevMeds = () => {
  return (
    getEligibleMedProducts({
      checkBMI: true,
      checkHealthRisks: false,
      checkState: false,
      checkFlow: true,
      requirePreviousMedicationsAnswer: false,
      requireHighestBMIAnswer: false,
      disabledNamedChecks: [
        "isQualifiedForPD",
        "isNotEligibleForCompoundedSemaglutide",
      ],
    }).length > 0
  );
};

export const isCurrentHealthRiskNoomClinicalEligible = () => {
  return (
    getEligibleMedProducts({
      checkBMI: false,
      checkHealthRisks: true,
      checkState: false,
      checkFlow: true,
      requirePreviousMedicationsAnswer: false,
      requireHighestBMIAnswer: false,
      disabledNamedChecks: [
        "isQualifiedForPD",
        "isNotEligibleForCompoundedSemaglutide",
      ],
    }).length > 0
  );
};

export const isEligibleForMedAfterHealthRiskQuestion = () => {
  return (
    getEligibleMedProducts({
      checkBMI: true,
      checkHealthRisks: true,
      checkState: false,
      checkFlow: true,
      requirePreviousMedicationsAnswer: true,
      requireHighestBMIAnswer: true,
      disabledNamedChecks: [
        "isQualifiedForPD",
        "isNotEligibleForCompoundedSemaglutide",
      ],
    }).length > 0
  );
};

export const isEligibleForMedAfterZipcodeQuestion = () => {
  return (
    getEligibleMedProducts({
      checkBMI: true,
      checkHealthRisks: true,
      checkState: true,
      checkFlow: true,
      requirePreviousMedicationsAnswer: true,
      requireHighestBMIAnswer: true,
      disabledNamedChecks: [
        "isQualifiedForPD",
        "isNotEligibleForCompoundedSemaglutide",
      ],
    }).length > 0
  );
};

export const isYoungerThan18 = ({ surveyAnswers } = getStore().getState()) => {
  const { dateOfBirth } = surveyAnswers;
  const birthDate = new Date(dateOfBirth);
  const age = calculateAge(birthDate);

  return age < 18;
};

export const isAgeEligibleForMed = (age: number) => {
  return age >= 18 && age < 80;
};

export const isAgeFromDobEligibleForMed = (
  { surveyAnswers } = getStore().getState()
) => {
  const { dateOfBirth } = surveyAnswers;
  const birthDate = new Date(dateOfBirth);
  const age = calculateAge(birthDate);

  return isAgeEligibleForMed(age);
};

export const isAgeEligibleForMht = (age: number) => {
  return age >= 45 && age < 60;
};

export const willReceiveMenstrualCycleModule = (
  { surveyAnswers } = getStore().getState()
) => {
  const { menstrualCycle } = surveyAnswers;
  return ["yes", "preferNotToSay"].includes(menstrualCycle?.[0]);
};

export const isMedLead = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get("utm_source");
  return utmSource === "MedDrip";
};

export const noomClinicalOptedIn = (
  { surveyAnswers } = getStore().getState()
) => {
  const { medOptIn } = surveyAnswers;
  // Note (zhuowei): For med drip users, always assume that they have opted in to med
  return medOptIn?.[0] === "yes" || isMedLead();
};

export const isZipcodeLabsExcluded = (
  { surveyAnswers } = getStore().getState()
) => {
  const { zipcode } = surveyAnswers;
  if (zipcode !== undefined) {
    const state = zipState(zipcode);
    return state in StatesLabsNotIncluded;
  }
  return false;
};

export const isMedicareStatusNoomClinicalEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  return !surveyAnswers.medicare?.includes("yes");
};

export const isGlp1StatusNoomClinicalEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  return surveyAnswers.glp1?.includes("no");
};

export const isAgeRangeStatusNoomClinicalEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  return !surveyAnswers.ageRange?.includes("80s+");
};

export const isPregnantStatusNoomClinicalEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  return !surveyAnswers.pregnant?.includes("noButNursing");
};

export const isPregnantStatusHealthCheckEligible = (
  { surveyAnswers: { pregnant } } = getStore().getState()
) => {
  return !["noButNursing", "yes"].includes(pregnant?.[0]);
};

export const isEatingDisorderStatusNoomClinicalEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  return surveyAnswers.eatingDisorder?.[0] !== "yes";
};

export const isMedicareStatusNoomClinicalEligibleAtCheckout = () => {
  return (
    !(hasSelectedBrandedPlan() || hasSelectedZepboundVialPlan()) ||
    isMedicareStatusNoomClinicalEligible()
  );
};

export const shouldSeeGlp1Companion = (state = getStore().getState()) => {
  const { surveyAnswers } = state;
  const glp1Answer = surveyAnswers?.glp1?.[0];
  const answeredToGLP1Question =
    hasAnsweredToGLP1FollowUpInCurrentSession("no");
  return (
    answeredToGLP1Question ||
    (!answeredToGLP1Question &&
      (glp1Answer === "yes" || glp1Answer === "prescription"))
  );
};

export const isEligibleForClinicalBuyflow = checkSegments({
  allOf: [isClinicalRoute, isUS, isEnLanguage],
});

export const showMedIneligiblePage = (state = getStore().getState()) => {
  const eligibleSaleItems = Boolean(
    state.recommendationContext?.eligibleSaleItems?.length > 0
  );
  return (
    !isEligibleForClinicalBuyflow(state) ||
    (isB2BUpsellClinicalRoute(state) && !eligibleSaleItems)
  );
};

export function isStandaloneBuyflowHRTEnabled() {
  return (
    getMedFlagOverride("standaloneHRTBuyflowEnabledOverwrite") ??
    !!appConfig.STANDALONE_HRT_BUYFLOW_ENABLED
  );
}

export const isEligibleForStandaloneHRTBuyflow = checkSegments({
  allOf: [isHRTRoute, isUS, isEnLanguage, isStandaloneBuyflowHRTEnabled],
});

export function isHealthRouteEnabled() {
  return (
    getMedFlagOverride("healthRouteEnabledOverwrite") ??
    !!appConfig.HEALTH_ROUTE_ENABLED
  );
}

export const isEligibleForHealthBuyflow = checkSegments({
  allOf: [isHealthRoute, isUS, isEnLanguage, isHealthRouteEnabled],
});

export function isCompounded503aTransitionEnabledWithOverride() {
  return (
    getMedFlagOverride("compounded503aTransitionEnabledOverwrite") ??
    !!appConfig.MED_COMPOUNDED_503B_TO_503A_PHARMACY_TRANSITION
  );
}

export const isCompounded503aTransitionEnabled = checkSegments({
  allOf: [isCompounded503aTransitionEnabledWithOverride],
});

export function isAddressChangeEnabledWithOverride() {
  return (
    getMedFlagOverride("addressChangeEnabledOverwrite") ??
    !!appConfig.MED_IS_ADDRESS_CHANGE_ENABLED
  );
}

export const isAddressChangeEnabled = checkSegments({
  allOf: [isAddressChangeEnabledWithOverride],
});

export const isUserNoomClinicalEligibleAtCheckout = checkSegments({
  allOf: [
    isEligibleForClinicalBuyflow,
    isBMINoomClinicalEligible,
    isMedicareStatusNoomClinicalEligibleAtCheckout,
    isCurrentHealthRiskNoomClinicalEligible,
  ],
});

export const isUserEligibleForHealthBuyflowAtCheckout = (
  state = getStore().getState()
) => {
  const { recommendationContext } = state;
  if (
    !isEligibleForHealthBuyflow() ||
    recommendationContext?.recommendedHealthPlan === NON_MED_PLAN.FREEMIUM
  ) {
    return false;
  }
  if (hasSelectedNoomWeightPlan()) {
    return true;
  }
  return isEligibleForMedProducts();
};

export const shouldShowNoomClinicalCheckoutOffer = (
  state = getStore().getState()
): boolean => {
  const { promoCode } = state;
  return (
    isUserNoomClinicalEligibleAtCheckout(state) &&
    !promoCode.promoCodeIsVip &&
    noomClinicalOptedIn(state)
  );
};

export const isInAppMedUpgrade = checkSegments({
  allOf: [
    ({ linearBuyflow }) =>
      getPageSet(linearBuyflow.pageSetName)?.layer === noomMedOfferLayer,
  ],
});

export const isDisabledNWSwitchForMed = () => {
  return (
    isInAppMedUpgrade() ||
    isAppUpsell() ||
    isCalorieClinicalRoute() ||
    getFeatureFlagValue("disableNWSwitchForMed")
  );
};

export const isUserSegmentEligibleForMedSwitchBuyflow = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [
    isCore,
    isReferralsRoute,
    isAppBaseRoute,
    isInAppWinback,
    isAffiliate,
    isFreemiumBranchedRoute,
  ],
});

export const isEligibleForMedSwitchBuyflow = checkSegments({
  allOf: [
    isUserSegmentEligibleForMedSwitchBuyflow,
    passesMinBMIForMedSwitchingBuyflow,
    isGlp1StatusNoomClinicalEligible,
    isAgeRangeStatusNoomClinicalEligible,
    isPregnantStatusNoomClinicalEligible,
  ],
  noneOf: [hasSignedLiabilityWaiver],
});

export const isNoomWeightMedBranching = checkSegments({
  allOf: [
    ({ linearBuyflow }) =>
      getPageSet(linearBuyflow.pageSetName)?.layer ===
      noomWeightMedBranchingLayer,
  ],
});

export const isWeightToMedRedirect = () => {
  const { isWeightToMedSwitch } = getSessionState("browser");
  return Boolean(isWeightToMedSwitch);
};

export const disableAccountFieldsForMedFlow = () => {
  const { disableAccountFieldsForMed } = getSessionState("browser");
  return Boolean(disableAccountFieldsForMed) && isClinicalRoute();
};

export const isPregnantStatusEligibleForHRT = () => {
  const { pregnant } = getSurveyAnswers();
  return pregnant?.includes("no");
};

export const isUserNoomHRTEligibleAtCheckout = checkSegments({
  allOf: [
    isEligibleForStandaloneHRTBuyflow,
    isAgeRangeEligibleForHRT,
    isLastPeriodEligibleForHRT,
    isPeriodEndReasonEligibleForHRT,
    isMenopauseSymptomsEligibleForHRT,
    isPregnantStatusEligibleForHRT,
    isEligibleForHrtAfterZipcodeQuestion,
    isEligibleForHrtAfterHealthRiskQuestion,
  ],
});

export const showMedCheckout = checkSegments({
  oneOf: [
    shouldShowNoomClinicalCheckoutOffer,
    isUserNoomHRTEligibleAtCheckout,
    isUserEligibleForHealthBuyflowAtCheckout,
  ],
});

export function isTelehealthPlan(state = getStore().getState()): boolean {
  const noomPlanId = state?.recommendedPlan?.noom_plan_id;
  const paidTelehealthPlans = Object.values(
    planDurationMonthsToTelehealthNoomPlanId
  );
  return (
    paidTelehealthPlans?.includes(noomPlanId) ||
    noomPlanId === TELEHEALTH_FREE_PLAN_ID
  );
}

export function isRecommendedPlanMed(state = getStore().getState()): boolean {
  const noomPlanId = state?.recommendedPlan?.braintree_id;
  return isMedTierPlanId(noomPlanId);
}

export function userHasPurchasedNoomClinical(state = getStore().getState()) {
  const { purchasedNoomClinical, userData } = state;

  // TODO: Move to using a more reliable way like an API instead of using
  // this userdata flag to determine who purchased Noom Med
  return purchasedNoomClinical || userData.purchasedNoomClinical;
}

const TELEHEALTH_CONTEXT_TYPES = ["telehealth", "telehealth-followup"];

export function isTelehealth() {
  return TELEHEALTH_CONTEXT_TYPES.includes(getMeristemContext().context_type);
}

/**
 * Signals if the current user/experience is subject to HIPPA data protection rules.
 * This can be used to filter logging/etc as appropriate to ensure compliance.
 */
export const isHipaa = (): boolean => {
  return isTelehealth();
};

export const isHmSuperswitchEligible = (): boolean => {
  const { currentSubscriptionData } = getStore().getState();
  return (
    isEnLanguage() &&
    currentSubscriptionData.hasActiveSubscription &&
    currentSubscriptionData.activeSubscriptionCurriculumSlug &&
    currentSubscriptionData.activeSubscriptionCurriculumSlug.endsWith(
      "en_US"
    ) &&
    !isMultiUserPlan(currentSubscriptionData?.planId) &&
    ((currentSubscriptionData.batchId === HW_NON_VIP_BATCH_ID_PROD &&
      appConfig.NOOM_ENV === "production") ||
      currentSubscriptionData.batchId === HW_NON_VIP_BATCH_ID_DEV)
  );
};

isHmSuperswitchEligible.load = () => {
  const store = getStore();
  const { email } = store.getState().userData;
  if (email) {
    return getStore()
      .dispatch(loadActiveSubscriptionDataState({ email }))
      .then(() => {});
  }
  return Promise.resolve();
};

export const isGlp1OrMed = (): boolean => {
  const { medEntitlements } = getStore().getState();
  return (
    medEntitlements.hasTelehealthAddOn ||
    medEntitlements.hasGlp1CoursePack ||
    medEntitlements.hasMedCompanion
  );
};

isGlp1OrMed.load = () => {
  return getStore()
    .dispatch(loadMedEntitlements({}))
    .then(() => {});
};

export const userHasAccessCode = (): boolean => {
  return !!getStore().getState().userData.access_code;
};

export const isInHouseSharePageEligible = checkSegments({
  allOf: [userHasAccessCode],
});

export const isEligibleForNoomTogether = checkSegments({
  allOf: [isEnLanguage, userHasAccessCode],
});

isEligibleForNoomTogether.load = async () => {
  await getStore().dispatch(loadActiveSubscriptionDataState({}));
};

export const hasFamilyPlanEligibleSubscription = () => {
  const { isIAPsubscription, hasActiveSubscription, planId } =
    getStore().getState()?.currentSubscriptionData;
  const peers =
    queryClient.getQueryData<MultiUserPlanPeerResponse[]>(
      GET_PEERS_CACHE_KEY
    ) || [];

  return (
    hasActiveSubscription &&
    !isIAPsubscription &&
    !isPremiumTierPlanId(planId) &&
    !isMultiUserPlan(planId) &&
    !userHasPurchasedNoomClinical() &&
    peers.length === 0
  );
};

hasFamilyPlanEligibleSubscription.load = () => {
  return Promise.all([
    getStore().dispatch(loadActiveSubscriptionDataState({})),
    loadPeers(),
  ]);
};

const isNotEnrolledInExR143 = (state: CoreReduxState) => {
  const { userData } = state;
  return !userData?.usedBaselinePriceForMultiUserPlan;
};

export const isFamilyPlanSurveyEligible = checkSegments({
  allOf: [
    isUS,
    isEnLanguage,
    hasFamilyPlanEligibleSubscription,
    isNotEnrolledInExR143,
  ],
});

export const isEligibleForExitIntentModalEmail = () => {
  return (
    isIntl() &&
    isEmailMain() &&
    visitTracker.getTrackingParams().utm_source === "drip"
  );
};

export function hasPromoCode(state?: CoreReduxState) {
  const promoCode = getPromoCodeState(state);
  return !!promoCode?.promoCodeApplied;
}

export function hasVipPromoCode(state?: CoreReduxState) {
  const promoCode = getPromoCodeState(state);

  return hasPromoCode() && promoCode?.promoCodeIsVip;
}

export const isTaxExclusiveCountry = () => isCountry(["US", "CA"]);

export const isNoomPremiumConsultationSurveyDisqualified = (
  state = getStore().getState()
) => {
  const { surveyAnswers } = state;
  return (
    surveyAnswers?.workoutPlanLimitations?.includes("painOrLosingBreath") ||
    surveyAnswers?.fitonWorkoutPlanLimitations?.includes("painOrLosingBreath")
  );
};

export const isNoomPremiumEligible = (state = getStore().getState()) => {
  const promoCode = getPromoCodeState();
  const isNoomPremiumEligibleBySegments =
    isNoomPremiumEligibleUS() || isNoomPremiumEligibleIntl();
  const isCurrentPlanPremiumTier = state.serverContext?.is_on_premium_tier_plan;
  return (
    !isCurrentPlanPremiumTier &&
    !promoCode?.promoCodeIsVip &&
    isNoomPremiumEligibleBySegments
  );
};

export const requiresDoubleOptInForEmail = () => {
  return isCountry(["DE", "AT"]);
};

export const isWinbackUserAndNotReup = () => {
  const { winbackInfo } = getStore().getState();

  return (
    ["TRIAL", "ADVANCED"].includes(winbackInfo.winbackStatus) &&
    !winbackInfo.willReup
  );
};

isWinbackUserAndNotReup.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const isWinbackUser = () => {
  const { winbackInfo } = getStore().getState();

  return ["TRIAL", "ADVANCED"].includes(winbackInfo.winbackStatus);
};

isWinbackUser.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const isReUp = (state = getStore().getState()) => {
  const { winbackInfo } = state;
  return winbackInfo.willReup;
};

isReUp.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const showPromoBox = checkSegments({
  noneOf: [routeConstants.exsf01, routeConstants.exsf01d, isWinback],
});

export function determinePlanOverride() {
  if (isDPP()) {
    return DPP_OVERRIDE_PLAN;
  }
  return "";
}

export const isDECoursePackEligible = () => {
  return isCountry(DE_COURSE_PACK_ELIGIBLE_COUNTRIES) && isDeLanguage();
};

const isRouteCoursePackEligible = checkSegments({
  oneOf: [
    isCore,
    isBaseline,
    isReferralsBuyflow,
    isEmailMain,
    isEmailWinback,
    isAppAndroidBase,
    isWinback,
    isAffiliate,
    isLegacyAffiliateTrialRoute,
  ],
});

export function mwCoursepackOptedIn(state = getStore().getState()) {
  const surveyAnswers = getSurveyAnswers(state);
  return surveyAnswers.noomMoodBundle?.includes("yes");
}

export function amCoursepackOptedIn(state = getStore().getState()) {
  const surveyAnswers = getSurveyAnswers(state);
  return surveyAnswers.agingMetabolismCoursepack?.includes("yes");
}

export function movementCoursepackOptedIn(state = getStore().getState()) {
  const surveyAnswers = getSurveyAnswers(state);
  return surveyAnswers.movementCoursepack?.includes("yes");
}

export function sleepCoursepackOptedIn(state = getStore().getState()) {
  const surveyAnswers = getSurveyAnswers(state);
  return surveyAnswers.sleepCoursepack?.includes("yes");
}

export function glp1CoursepackOptedIn(state = getStore().getState()) {
  const { surveyAnswers } = state;
  return (
    surveyAnswers.glp1?.includes("yes") ||
    surveyAnswers.glp1?.includes("prescription")
  );
}

const isENCoursePackEligible = () => {
  return isCountry(EN_COURSE_PACK_ELIGIBLE_COUNTRIES) && isEnLanguage();
};

const isLocaleCoursePackEligible = () => {
  return isENCoursePackEligible() || isDECoursePackEligible();
};

export function shouldShowCoursePackSurveys(state = getStore().getState()) {
  const { recommendedPlan } = state;
  return (
    isLocaleCoursePackEligible() &&
    isRouteCoursePackEligible() &&
    recommendedPlan.has_trial &&
    !isReUp(state) &&
    !showMedCheckout()
    // Originally we only showed course pack surveys if
    // you were not eligible for Noom Med. However we will support both
    // Moom Med and Mental Wellness, and selectively suppress
    // AM for now while 3 course packs support is built out.
  );
}

shouldShowCoursePackSurveys.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const isRouteEligibleForGLP1CEOfferOnly = checkSegments({
  oneOf: [isClinicalRoute, isAppAndroidWinback],
});

export const isEligibleForGLP1CEOfferOnly = checkSegments({
  allOf: [isRouteEligibleForGLP1CEOfferOnly, shouldSeeGlp1Companion],
});

export function shouldShowCoursePackCheckoutOffers(
  state = getStore().getState()
) {
  const { promoCode } = state;
  return (
    (shouldShowCoursePackSurveys() || isEligibleForGLP1CEOfferOnly()) &&
    !promoCode.promoCodeIsVip &&
    !showMedCheckout()
  );
}

export const shouldShowPaymentAddonCheckoutOffers = checkSegments({
  oneOf: [shouldShowCoursePackCheckoutOffers],
});

export const shouldPreselectMWCoursepackCheckoutOffer = checkSegments({
  oneOf: [
    // Via buyflow coursepack surveys
    (state) =>
      shouldShowCoursePackCheckoutOffers(state) && mwCoursepackOptedIn(state),
    // Via email route modal
    ({ userData }) =>
      userData?.answerToFreeMWCPModal === "yes" &&
      userData?.checkoutDiscountedMWCPTimerExpirationTimestamp &&
      userData.checkoutDiscountedMWCPTimerExpirationTimestamp > Date.now(),
  ],
  noneOf: [isReUp],
});

export const shouldPreselectAMCoursepackCheckoutOffer = checkSegments({
  allOf: [shouldShowCoursePackCheckoutOffers, amCoursepackOptedIn],
});

export const shouldPreselectSleepCoursepackCheckoutOffer = checkSegments({
  allOf: [shouldShowCoursePackCheckoutOffers, sleepCoursepackOptedIn],
});

export const shouldPreselectMovementCoursepackCheckoutOffer = checkSegments({
  allOf: [shouldShowCoursePackCheckoutOffers, movementCoursepackOptedIn],
});

export function acceptedMWCoursepackOffer(state = getStore().getState()) {
  const { checkout, paymentAddon } = state;
  return (
    checkout.paymentAddonsInCart.includes(
      paymentAddonProducts.mentalWellness
    ) &&
    Boolean(selectMWCoursepack(paymentAddon)) &&
    shouldShowCoursePackCheckoutOffers(state)
  );
}

export function acceptedAMCoursepackOffer(state = getStore().getState()) {
  const { checkout, paymentAddon } = state;
  return (
    checkout.paymentAddonsInCart.includes(
      paymentAddonProducts.agingMetabolism
    ) &&
    Boolean(selectAMCoursepack(paymentAddon)) &&
    shouldShowCoursePackCheckoutOffers(state)
  );
}

export function acceptedNoomClinicalOffer(state = getStore().getState()) {
  const { checkout } = state;
  return (
    isInAppMedUpgrade() ||
    (showMedCheckout(state) && checkout.acceptedClinicalOffer)
  );
}

export function acceptedSleepCoursepackOffer(state = getStore().getState()) {
  const { checkout, paymentAddon } = state;
  return (
    checkout.paymentAddonsInCart.includes(paymentAddonProducts.sleep) &&
    Boolean(selectSleepCoursepack(paymentAddon)) &&
    shouldShowCoursePackCheckoutOffers(state)
  );
}

export function acceptedMovementCoursepackOffer(state = getStore().getState()) {
  const { checkout, paymentAddon } = state;
  return (
    checkout.paymentAddonsInCart.includes(paymentAddonProducts.movement) &&
    Boolean(selectMovementCoursepack(paymentAddon)) &&
    shouldShowCoursePackCheckoutOffers(state)
  );
}

export function acceptedGlp1CompanionOffer(state = getStore().getState()) {
  const { checkout, paymentAddon } = state;
  return (
    checkout.paymentAddonsInCart.includes(paymentAddonProducts.glp1Companion) &&
    Boolean(selectGlp1Coursepack(paymentAddon))
  );
}

export function isEligibleForPersonalizedPlanTimer() {
  // Note(Pratik) inAppIos is the only route where they saw the original timer
  // but are not eligible for coursepacks to see the new timer due to itunes addon purchase
  // limitations so we will preserve their experience
  // Note (kevinh) DE coursepack users are not eligible for free plans so they will also
  // see the original timer function
  return (
    !isInAppEmployerSurvey() &&
    (isAppIosBase() || isDECoursePackEligible() || isAppIOSUpsell())
  );
}

export const shouldHideExistingPurchaseSurveyAnswers = checkSegments({
  allOf: [isPersonalizedPlanTimerExpired],
  noneOf: [isEmailMain, isEmailWinback],
});

export function isEligibleForCheckoutOfferTimer(state = getStore().getState()) {
  return (
    shouldShowCoursePackCheckoutOffers() &&
    !isEligibleForGLP1CEOfferOnly() &&
    Boolean(selectMWCoursepack(state.paymentAddon)) &&
    !isCheckoutOfferTimerExpired()
    // We only check for checkoutTimer expired in the case of checkout offer timer because we don't ever want to
    // re-show the timer once the user already has their offer expired, whereas with the personalized plan,
    // the user is entitled to another round of the timer if they go through the buyfow again
  );
}

export function isEligibleForCheckoutTimer() {
  return (
    !isOnAppleExternalLinkoutPath() &&
    (isEligibleForPersonalizedPlanTimer() || isEligibleForCheckoutOfferTimer())
  );
}

export function isNoomSummitEligible() {
  return false;
}

export const isProgramLocaleNoomSummitEligible = () => {
  // We should make sure we are getting this from the sever context call
  return isEnProgramLocale();
};

export const isNoomSummitDisplayableInStore = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [isTelehealthPlan, userHasPurchasedNoomClinical],
});

// Users who purchase Noom Summit can still visit the noom-summit-offer pagesets,
// and will be redirected to the confirmation page
export const isNoomSummitOfferEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [
    isTelehealthPlan,
    userHasPurchasedNoomClinical,
    isInExperimentConflictingWithSummitCoursePack,
  ],
});

export const isNoomSummitSurvey = checkSegments({
  allOf: [
    ({ linearBuyflow }) =>
      getPageSet(linearBuyflow.pageSetName)?.layer === noomSummitSurveyLayer,
  ],
});

export function hasBoughtMealPlan() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds
    .filter(Boolean)
    .some((p) => isMealPlan(p) || isMealWorkoutBundle(p));
}

export function hasBoughtWorkoutPlan() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds
    .filter(Boolean)
    .some((p) => isWorkoutPlan(p) || isMealWorkoutBundle(p));
}

export function hasBoughtMealAndWorkoutPlan() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds.filter(Boolean).some(isMealWorkoutBundle);
}

/**
 * Verifies if current user is on referral routes and wants a full buyflow
 */
export const isFullPersonalizationBuyflow = checkSegments({
  noneOf: [
    ({ surveyAnswers }: CoreReduxState) => {
      return surveyAnswers.customizationChoice?.[0] === "quickStart";
    },
  ],
});

const isPreSelectedPlanLongerThan1Month = (state: CoreReduxState) => {
  const { planOptions } = state;

  return planOptions?.preSelected?.billing_cycle_duration > 1;
};

const isPreSelectedPlanLongerThan2Months = (state: CoreReduxState) => {
  const { planOptions } = state;

  return planOptions?.preSelected?.billing_cycle_duration > 2;
};

const isPreSelectedPlanTrial14Days = (state: CoreReduxState) => {
  const { planOptions } = state;

  return planOptions?.preSelected?.trial_duration === 14;
};

export const isLoadingPromoCodeModalEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [isEmailMain, isEmailWinback],
});

export function shouldRedirectToEmployerSurvey(
  { surveyAnswers } = getStore().getState()
) {
  const correctSurveyAnswer =
    surveyAnswers.employmentLifestyle?.includes("fullTime");
  const isCorrectTargeting = isEnLanguage() && isUS();
  const meristemConfigValue = getMeristemConfigValue(
    "redirectToEmployerSurvey"
  );

  return correctSurveyAnswer && (meristemConfigValue ?? isCorrectTargeting);
}

export function shouldRedirectToPostCheckoutContext() {
  return !!getMeristemConfigValue("usePostCheckoutContext");
}

export function shouldRedirectToHmAddonsContext() {
  return !!getMeristemConfigValue("useHmAddonsContext");
}

export function shouldRedirectHmReferralsContext() {
  return !!getMeristemConfigValue("useHmReferralsContext");
}

export const shouldShowHealthyPaceHighlight = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle, isWinback],
});

export const shouldShowImportantEventHighlight = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle, isWebWinback],
});

export const isEligibleForNursingOption = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle],
});

// ex472b feature. PR: https://github.com/noom/buyflow-client/pull/1356
export const shouldShowAlternatePostCheckoutHeadersText = checkSegments({
  allOf: [isUS],
  oneOf: [isCore, isAffiliate, isAppBaseRoute, isReferralsBuyflow],
});

export const shouldShowUnloadWarning = checkSegments({
  allOf: [isUS],
  oneOf: [isCore, isAffiliate, isAppBaseRoute, isReferralsBuyflow],
});

export const isEligibleForAgingMetabolismSurvey = (
  state = getStore().getState()
) => {
  if (!isEligibleForNX1CourseEnhancements()) {
    return false;
  }
  const { ageRange, cognitiveExplore } = getSurveyAnswers(state);
  if (ageRange?.includes("20s")) {
    return false;
  }
  if (ageRange?.includes("30s")) {
    return (
      cognitiveExplore?.includes("aging") ||
      cognitiveExplore?.includes("metabolism")
    );
  }
  return true;
};

export const isEligibleForSleepAndMovementSurveys = () => {
  return isEligibleForNX1CourseEnhancements();
};

export const isEligibleForModularBuyflow = checkSegments({
  allOf: [isEnLanguage, isMantle],
  noneOf: [isHM],
});

const isEmailIntl = checkSegments({
  allOf: [isIntl],
  oneOf: [isEmailMain, isEmailWinback],
});

const isEmailWinbackUS = checkSegments({
  allOf: [isUS, isEmailWinback],
});

export const isFreeMonthsPlanOverrideEligible = checkSegments({
  allOf: [isEnLanguage],
  oneOf: [isEmailIntl, isEmailWinbackUS],
});

export const isFreeMonthsPlanSelectEligible = checkSegments({
  allOf: [isEnLanguage, isEmailMain, isUS],
});

export const shouldShowFreeMonthsPlanSelect = () => {
  return !!freeMonthsOfferConfiguration() && isFreeMonthsPlanSelectEligible();
};

// Free month offers are shown to users in 2 different forms:
// 1. Free month plan override that overrides a shorter plan with a longer plan
// of cheaper or equal price when the user applies a promocode
//
// 2. Free month plan select which shows a 3 plan selection UI (monthly, middle length, long length)
// and discounts the long length plan to make it equal to the price of the middle length plan
// when the user applies a promocode
export const isFreeMonthsOfferEligible = checkSegments({
  oneOf: [isFreeMonthsPlanOverrideEligible, isFreeMonthsPlanSelectEligible],
});

export const isPlanSelectionEligible = checkSegments({
  oneOf: [
    shouldShowFreeMonthsPlanSelect,
    checkSegments({
      allOf: [
        isEmailMain,
        isUS,
        isPreSelectedPlanLongerThan1Month,
        isPreSelectedPlanTrial14Days,
      ],
    }),
  ],
});

export const shouldShowPlanSelectionSavings = checkSegments({
  allOf: [isPlanSelectionEligible, isPreSelectedPlanLongerThan2Months],
});

export const hasExistingAcccount = checkSegments({
  oneOf: [isAppEmail, isUpsellRoute, disableAccountFieldsForMedFlow],
});

const isCurrentPlanForced = (state: CoreReduxState = getStore().getState()) => {
  const { recommendedPlan } = state;
  const { isForcedPlan } = recommendedPlan;
  return !!isForcedPlan;
};

export const isMultiUserPlanEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [
    isCore,
    isReferralsBuyflow,
    isWinback,
    isAppAndroidBase,
    isAffiliateWithTrial,
  ],
  noneOf: [
    isHolidayPromotion,
    isCurrentPlanForced,
    isEligibleForCoachReferralBuyflow,
    isAffiliateWithLongTrial,
  ],
});

export function multiUserPlanOptedIn(
  state: CoreReduxState = getStore().getState()
) {
  const TWO_USER_PLAN = "twoUserPlan";
  const { nominationFamily } = state.surveyAnswers;

  return nominationFamily?.[0] === TWO_USER_PLAN;
}

export const shouldShowPrimedSupporterExperience = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [
    isCore,
    isAffiliate,
    isAppBaseRoute,
    isReferralsBuyflow,
    isWebWinback,
    isEmailMain,
    isClinicalRoute,
  ],
});

export const isReferedFromUserPortal = () => {
  return getURLParams()?.redirected_from === "userportal";
};

export const isCampaignFeatureOura = () => {
  return getURLParams()?.feature === "oura";
};

export const isFallbackPromotionalOfferEligible = checkSegments({
  allOf: [isUS, isEnLanguage, isEmailMain],
});

const wasIncorrectPromoCodeEntered = (
  state: CoreReduxState = getStore().getState()
) => {
  return state?.promoCode?.promoCodeErrorType === "IncorrectPromoCode";
};

export const shouldShowFallbackPromotionalOffer = checkSegments({
  allOf: [
    isFallbackPromotionalOfferEligible,
    hasFallbackPromotionalOfferConfiguration,
    wasIncorrectPromoCodeEntered,
  ],
  noneOf: [shouldShowFreeMonthsPlanSelect],
});

export const shouldAllowPromoCodeRemoval = () => {
  if (
    isFallbackPromotionalOfferEligible() &&
    hasFallbackPromotionalOfferConfiguration()
  ) {
    return true;
  }

  return (
    (isPromoCodePersisted() || !!getCampaignFromStorage()) &&
    isEnLanguage() &&
    (isEmailMain() || isEmailWinback())
  );
};

export function canUseShareApi() {
  return canUseNavigatorShare() || canUseMobileSharingInterface();
}

export const isBillingReminderEligible = checkSegments({
  oneOf: [
    isCore,
    isAffiliateWithTrial,
    isAppAndroidBase,
    isReferralsBuyflow,
    isAppIosBase,
    isApplePurchaseLinkoutEnabled,
  ],
  allOf: [isUS, isEnLanguage],
});

export const isEligibleForPreCheckoutFreeMWCPOffer = checkSegments({
  allOf: [isUS, isEnLanguage, isPreSignup],
  oneOf: [isEmailMain, isEmailWinback],
  noneOf: [isReUp],
});

export const isCareFirstRedirectEligible = () => {
  return (
    isUS() &&
    isEnLanguage() &&
    ["MD", "DC", "DE", "VA"].includes(getSubdivision())
  );
};

export const isMHMDAState = () => {
  return isUS() && MHMDA_STATES.includes(getSubdivision());
};

export const isGDPRCountry = () => {
  return isAU() || isCA() || isEU() || isNZ();
};

export const isEligibleForHealthInsuranceProviderQ = (): boolean => {
  const { surveyAnswers } = getStore().getState();
  const { currentBMI } = calculateBMI(surveyAnswers, false);
  return currentBMI > 25;
};

export const isFromCareFirstEmailBlast = () => {
  return (
    isUS() &&
    isEnLanguage() &&
    isFromEmail() &&
    (getURLParams()?.utm_source === "cf_drip" ||
      getURLParams()?.utm_campaign === "cf_blast")
  );
};

export const isUserEnrolledInMABExperiment = () => {
  const mabKey = window.localStorage.getItem(BANDIT_LOCAL_STORAGE_KEY);
  return !!(mabKey && getFeatureFlagValue("enrolledInMABExperiment"));
};

export const isGlpEligible = checkSegments({
  allOf: [isENCoursePackEligible],
  noneOf: [isUK],
  oneOf: [
    isStrictCore,
    isWinback,
    isReferralsBuyflow,
    isAffiliate,
    isClinicalRoute,
    isLegacyAffiliateTrialRoute,
    isAppAndroid,
    isAppEmail,
    isFromNoomWeightEmail,
    isBaseline,
    isAdWordsSEM,
    isFreemiumUpsellRoute,
    isAppIosBaseAndWinback,
  ],
});

export function isOwnSupporter(state: CoreReduxState = getStore().getState()) {
  const { supporterEmail } = getSurveyAnswers(state);
  const { email } = state.userData;

  return email === supporterEmail && !!email;
}

export const shouldUseYesOrNoQuestion = checkSegments({
  allOf: [isEnMantle],
  noneOf: [isEmailWinback, isEmailMain, isClinicalRoute],
});

export const shouldShowWomensHealthSurveyQuestions = checkSegments({
  allOf: [isUS, isFemaleSex],
  oneOf: [isAppIosBase, isFreemiumWeightRoute],
});

export const showPaymentInfoAccordion = (
  state: CoreReduxState = getStore().getState()
) => {
  return (
    isApplePurchaseLinkoutEnabled() ||
    (!hasVipPromoCode(state) && !isInAppNativePurchaseEligible(state))
  );
};

export const showAccountInfoAccordion = checkSegments({
  oneOf: [shouldEnrollUser, shouldShowNoomClinicalCheckoutOffer],
});

export const isEligibleForNewEmailFlow = checkSegments({
  allOf: [isFromNoomWeightEmail, isUS, isEnLanguage],
});

export const showSexQuestionOnEmail = createOneTimeConditionSegment(
  "sex",
  (state) => {
    const surveyAnswers = getSurveyAnswers(state);
    return !surveyAnswers.sex;
  }
);

export const showHeightQuestionOnEmail = createOneTimeConditionSegment(
  "basicHeight",
  (state) => {
    const surveyAnswers = getSurveyAnswers(state);
    return !surveyAnswers.heightCm;
  }
);

export const showPregnantQuestionOnEmail = createOneTimeConditionSegment(
  "pregnant",
  (state) => {
    const surveyAnswers = getSurveyAnswers(state);
    return !surveyAnswers.pregnant || surveyAnswers.pregnant?.[0] === "yes";
  }
);

export const showEatingDisorderQuestionOnEmail = createOneTimeConditionSegment(
  "eatingDisorder",
  (state) => {
    const surveyAnswers = getSurveyAnswers(state);
    return (
      !surveyAnswers.eatingDisorder ||
      surveyAnswers.eatingDisorder?.[0] === "yes"
    );
  }
);

export const showEmailQuestionOnEmail = createOneTimeConditionSegment(
  "email",
  (state) => {
    const { userData } = state;
    return !isEmail(userData.email);
  }
);

export function shouldDisplayWLGModal() {
  return (
    isUS() &&
    isChooseTrialPriceNewStyleEligible() &&
    !isHM() &&
    !getFeatureFlagValue("disableWLGModal") &&
    isClinicalRoute()
  );
}

export function shouldShowExNV52() {
  return !!getMeristemConfigValue("isExNV52Active");
}

export function shouldShowTelex225V2() {
  return !!getMeristemConfigValue("isTelex225V2Active");
}

export function isDiabetesSupportActive() {
  return !!getMeristemConfigValue("isDiabetesSupportActive");
}

export const isProbioticsDeliveryEligible = checkSegments({
  allOf: [isEnLanguage, isUS],
  oneOf: [
    isCore,
    isWinback,
    isReferralsRoute,
    isFromNoomWeightEmail,
    isAffiliateWithTrial,
    isAppAndroidBase,
    isCoachReferralsRoute,
  ],
});

export const isCountryEligibleForZumba = checkSegments({
  oneOf: [isUS, isCA, isAU, isNZ],
});

export const isCurrentTrialZumbaEligible = (
  { userData, serverContext } = getStore().getState()
) => {
  const { purchased_fast_forward_trial } = serverContext;
  const { growthTrialEndDateISO, growthProgramStartDateISO } = userData;
  const trialSeconds =
    new Date(growthTrialEndDateISO).getTime() -
    new Date(growthProgramStartDateISO).getTime();
  const trialDays = Math.round(trialSeconds / (1000 * 3600 * 24));

  return purchased_fast_forward_trial || [0, 7, 14].includes(trialDays);
};

function isZumbaOfferEnabled() {
  return !!appConfig.ZUMBA_OFFER_ENABLED;
}

export const isZumbaOfferEligible = checkSegments({
  allOf: [
    isEnLanguage,
    isCountryEligibleForZumba,
    isCurrentTrialZumbaEligible,
    isZumbaOfferEnabled,
  ],
  oneOf: [
    isCore,
    routeConstants.winback,
    isAppAndroidBase,
    isAffiliateWithTrial,
    isEmailMain,
    isEmailWinback,
    isReferralsRoute,
    isCoachReferralsRoute,
  ],
});

export function shouldSuppressStepTrackingVibeOffer() {
  return shouldSuppressVibeOffer(STEP_TRACKING_VIBE_OFFER_ID);
}

export function isEligibleForStepTrackingVibeOffer() {
  const { surveyAnswers } = getStore().getState();

  return (
    ((surveyAnswers.stepTracking?.[0] === "yes" && surveyAnswers.dailySteps) ||
      surveyAnswers.stepTracking?.[0] === "no") &&
    !isClinicalRoute() &&
    isEnLanguage()
  );
}

export function shouldShowStepTrackingVibeOffer() {
  return (
    isEligibleForStepTrackingVibeOffer() &&
    !shouldSuppressStepTrackingVibeOffer()
  );
}

export function isEligibleForNoomPremiumNoTrial() {
  const meristemFlag = getMeristemConfigValue("premiumSubForNoTrialHw");
  return !(meristemFlag || isPremiumTextCoachingSubscriptionEligible());
}

export function isReferralLinkFlowEnabled() {
  return !!appConfig.REFERRAL_LINK_FLOW_ENABLED;
}

export const isEligibleForReferralLinkPage = checkSegments({
  oneOf: [isUS, isCA, isUK, isAU, isNZ],
  allOf: [isEnLanguage, isReferralLinkFlowEnabled],
});

export function shouldSuppressGLP1Question() {
  return hasAnsweredToGLP1FollowUpInCurrentSession("no");
}

export function isSubscriptionPremiumTier() {
  const { serverContext } = getStore().getState();
  return isPremiumTierPlanId(serverContext.current_plan_id);
}

export function isSubscriptionMedTier() {
  const { serverContext } = getStore().getState();
  return isMedTierPlanId(serverContext.current_plan_id);
}

export function userHasActivePremiumWithTextCoachingAddon() {
  const { serverContext } = getStore().getState();
  return !!serverContext.addon_subscriptions?.find((addon) => {
    return (
      addon.product === "PREMIUM_WITH_TEXT_COACHING" &&
      addon.status === "ACTIVE"
    );
  });
}

export function userHasActivePremiumBundleAddon() {
  const { serverContext } = getStore().getState();
  return !!serverContext.addon_subscriptions?.find((addon) => {
    return (
      addon.product === "MEAL_WORKOUT_PLANNING" && addon.status === "ACTIVE"
    );
  });
}

export function userHasPremiumPlan() {
  return (
    isSubscriptionPremiumTier() ||
    userHasActivePremiumWithTextCoachingAddon() ||
    userHasActivePremiumBundleAddon()
  );
}

export function userHasActiveSubscription() {
  const { currentSubscriptionData } = getStore().getState();
  return currentSubscriptionData.hasActiveSubscription;
}

userHasActiveSubscription.load = async () => {
  await getStore().dispatch(loadActiveSubscriptionDataState({}));
};

export function isEligibleForCoachReferralBuyflow() {
  const coachReferralInfo = getCoachReferralInfo();
  return (
    isCoachReferralsRoute() &&
    isEligibleForReferralLinkPage() &&
    !!coachReferralInfo
  );
}

export function getMedFlagOverride(medFlag: string) {
  const urlParams = getURLParams();
  if (isDevMode() && urlParams && urlParams[medFlag] != null) {
    return urlParams[medFlag] === "true";
  }
  return undefined;
}

export function isMicrodosePromoExperimentEnabled() {
  if (isInAppMedUpgrade()) {
    return false;
  }
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("f_mds_pv79") === "true";
}

export function isCompoundedPromoExperimentEnabled() {
  // For both compounded and liraglutide
  if (isInAppMedUpgrade()) {
    return false;
  }
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("f_glp_pv129") === "true";
}

export function shouldHideFitonWorkoutPlanIntensity() {
  const { surveyAnswers } = getStore().getState();

  return (
    (isMaleGender() &&
      surveyAnswers.fitonWorkoutPlanDumbbells?.includes("hasNoDumbbells")) ||
    (!isMaleGender() &&
      surveyAnswers.fitonWorkoutPlanDuration?.includes("lessThan20Min"))
  );
}

export function isZipcodeCorrespondingToUSState(
  { surveyAnswers } = getStore().getState()
) {
  const { zipcode } = surveyAnswers;
  if (zipcode !== undefined) {
    const state = zipState(zipcode);
    return state !== null;
  }
  return false;
}

export function isEligibleForMedProducts() {
  return getEligibleMedProducts().length > 0;
}

export function isEligibleForMoreThanOneMedProduct() {
  return getEligibleMedProducts().length > 1;
}

export function hasSelectedBrandedPlan() {
  return (
    getMedFlagOverride("selectBrandedPlan") ??
    getSessionState("browser").selectedMedPlanType === "branded"
  );
}

export function hasSelectedCompoundedPlan() {
  return (
    getMedFlagOverride("selectCompoundedPlan") ??
    getSessionState("browser").selectedMedPlanType === "compounded"
  );
}

export function hasSelectedOralsMetforminPlan() {
  return (
    getMedFlagOverride("selectOralsMetforminPlan") ??
    getSessionState("browser").selectedMedPlanType === "oralsMetformin"
  );
}

export function hasSelectedZepboundVialPlan() {
  return (
    getMedFlagOverride("selectZepboundVialPlan") ??
    getSessionState("browser").selectedMedPlanType === "brandedZepboundVial"
  );
}

export function hasSelectedLiraglutidePlan() {
  return (
    getMedFlagOverride("selectLiraglutidePlan") ??
    getSessionState("browser").selectedMedPlanType === "genericLiraglutide"
  );
}

export function hasSelectedMicrodosePlan() {
  return (
    getMedFlagOverride("selectMicrodosePlan") ??
    getSessionState("browser").selectedMedPlanType === "compoundedMicrodose"
  );
}

export function hasSelectedMicrodoseBiomarkerPlan() {
  return (
    getMedFlagOverride("selectMicrodoseBiomarkerPlan") ??
    getSessionState("browser").selectedMedPlanType ===
      "compoundedMicrodoseProactiveHealthBiomarker"
  );
}

export function hasSelectedNoomWeightPlan() {
  return !!getSessionState("browser").selectedNoomWeightPlanOption;
}

export function isCoachReferralAcceptingNewUsers() {
  const referralInfo = getCoachReferralInfo();
  return !!referralInfo.acceptingNewUsers;
}

const NMC_CONTEXT_TYPES = ["nmc-landing", "nmc-survey", "nmc-coming-soon"];

export function isNmc() {
  return NMC_CONTEXT_TYPES.includes(getMeristemContext().context_type);
}

export function isNoomPremiumInAppUpgradeEligible() {
  const { planId, activeSubscriptionCurriculumSlug, isIAPsubscription } =
    getLoadedSubscriptionStatus() || {};

  return (
    !isMultiUserPlan(planId) &&
    !isIAPsubscription &&
    !planId?.endsWith("__pause") &&
    activeSubscriptionCurriculumSlug?.startsWith(
      HEALTHY_WEIGHT_CURRICULUM_PREFIX
    )
  );
}

isNoomPremiumInAppUpgradeEligible.load = async () => {
  await loadSubscriptionStatus();
};

export function isCompoundedPlanIneligible() {
  const params = new URLSearchParams(window.location.search);
  const forcedIneligibility = params.get("disableCompoundedOffer") === "true";

  return (
    forcedIneligibility ||
    !getEligibleMedProducts().includes(MED_SKU.COMPOUNDED)
  );
}

export function isLiraglutidePlanIneligible() {
  return !getEligibleMedProducts().includes(MED_SKU.GENERIC_LIRAGLUTIDE);
}

export function isCompoundedAndLiraglutidePlanIneligible() {
  return isCompoundedPlanIneligible() && isLiraglutidePlanIneligible();
}

export function isMicrodosePlanIneligible() {
  return !getEligibleMedProducts().includes(MED_SKU.COMPOUNDED_MICRODOSE);
}

export function isPremiumConfigurationWithCoaching() {
  const { planId } = getLoadedSubscriptionStatus() || {};

  return isPremiumTierPlanId(planId);
}

isPremiumConfigurationWithCoaching.load = async () => {
  await loadSubscriptionStatus();
};

export function isPremiumUpgradeSourceBuyflow() {
  const { userData } = getStore().getState();
  return userData?.premiumUpgradeSource === "BUYFLOW";
}

export const isPremiumTierCounterOfferEligible = checkSegments({
  allOf: [isSubscriptionPremiumTier, isPremiumUpgradeSourceBuyflow],
  noneOf: [isSubscriptionMedTier, isSubscriptionInvalid],
});

export function isBrandedPlanIneligible() {
  return !getEligibleMedProducts().includes(MED_SKU.BRANDED);
}

export function isZepboundVialPlanIneligible() {
  return !getEligibleMedProducts().includes(MED_SKU.BRANDED_ZEPBOUND_VIAL);
}

export function isOralsPlanIneligible() {
  return !getEligibleMedProducts().includes(MED_SKU.ORALS_METFORMIN);
}

export const showHeightQuestionOnInAppUpgrade = createOneTimeConditionSegment(
  "basicHeight",
  (state) => {
    const surveyAnswers = getSurveyAnswers(state);
    return !surveyAnswers.heightCm;
  }
);

export const showIdealWeightQuestionOnInAppUpgrade =
  createOneTimeConditionSegment(
    "idealWeightKg",
    (state) => !hasIdealWeight(state)
  );

export function hasIdealWeight(state = getStore().getState()) {
  const surveyAnswers = getSurveyAnswers(state);
  return !!surveyAnswers.idealWeightKg;
}

export const showSexQuestionOnInAppUpgrade = createOneTimeConditionSegment(
  "sex",
  (state) => {
    const surveyAnswers = getSurveyAnswers(state);
    return !surveyAnswers.sex;
  }
);

export function isIneligibleForMedUpgrade() {
  const { serverContext } = getStore().getState();
  return (
    !serverContext.is_eligible_for_med_upgrade ||
    getEligibleMedProducts({
      checkBMI: false,
      checkHealthRisks: false,
      checkState: true,
      checkFlow: true,
      requirePreviousMedicationsAnswer: false,
      requireHighestBMIAnswer: false,
      disabledNamedChecks: [
        "isQualifiedForPD",
        "isNotEligibleForCompoundedSemaglutide",
      ],
    }).length === 0
  );
}

export function isEligibleForNoomPremiumInMedUpgradeFlow() {
  const { serverContext } = getStore().getState();
  return (
    serverContext.is_eligible_for_premium_upgrade && !!getInAppAutoCookie()
  );
}

export const isEligibleForFreemiumIUBuyflow = checkSegments({
  oneOf: [isUS, isCA, isAU],
  allOf: [isFreemiumIdentifiedRoute, isEnLanguage],
});

export const isEligibleForFreemiumUnidentifiedBuyflow = checkSegments({
  oneOf: [isAppUS, isAppCA, isAppAU],
  allOf: [isFreemiumUnidentifiedRoute, isEnLanguage],
});

// checks if any freemium route
// (unidentified user who wants free bodyscan experience should be added here)
export const isEligibleForFreemium = checkSegments({
  oneOf: [
    isEligibleForFreemiumIUBuyflow,
    isEligibleForFreemiumUnidentifiedBuyflow,
  ],
});

export const isFacialBloodScanSurvey = checkSegments({
  allOf: [
    ({ linearBuyflow }) =>
      getPageSet(linearBuyflow.pageSetName)?.layer === "facial-blood-scan",
  ],
});

export function shouldShowFirstNameQuestion() {
  // Apple requires that the firstName question is skipped
  // if users sign in with Apple. Backend will set inAppName in
  // the userData for this flow so we can use it to identify and skip.
  // See https://noomhq.atlassian.net/browse/GTIER-166.
  const { userData } = getStore().getState();
  return !isEligibleForFreemiumUnidentifiedBuyflow() && !userData.inAppName;
}

export function isRedesignEnabled() {
  return (
    !isClinicalRoute() &&
    (isEligibleForFreemiumIUBuyflow() ||
      isFreemiumQuizRoute() ||
      isFacialBloodScanSurvey())
  );
}

export function isOralsMedPaidTraffic() {
  const params = new URLSearchParams(window.location.search);
  return params.get("feature") === "orals";
}

export function getMedSwitchFlowToParam() {
  const params = new URLSearchParams(window.location.search);
  return params.get("switchTo")?.toUpperCase();
}

export function shouldFetchAuthenticatedUserData() {
  return getActiveUser() !== null && isUpsellRoute();
}

export function isEligibleForNoomMedSwitch() {
  const switchToParam = getMedSwitchFlowToParam();

  const isValidSwitchToParam =
    switchToParam &&
    ELIGIBLE_SKUS_FOR_SWITCH.includes(switchToParam as MED_SKU);
  return isValidSwitchToParam;
}

export const isLDSAudienceCampaign = () => {
  return (
    isEligibleForClinicalBuyflow() && getURLParams()?.feature === "ldsAudience"
  );
};

export function getMhtForcedParam() {
  const params = new URLSearchParams(window.location.search);
  return params.get("enableMhtFlow");
}

export function isHRTPromoLPTraffic() {
  const params = new URLSearchParams(window.location.search);
  return params.get("feature") === "hrt";
}

export const isRouteEligibleForMhtBuyflow = checkSegments({
  oneOf: [
    isCore,
    isAffiliateWithTrial,
    isReferralsBuyflow,
    isWebWinback,
    isEmailMain,
    isAppAndroidBase,
  ],
});

export const isUserSegmentEligibleForMhtBuyflow = checkSegments({
  allOf: [isUS, isEnLanguage, isRouteEligibleForMhtBuyflow],
});

export const isEligibleForHRTBuyflow = checkSegments({
  allOf: [isUserSegmentEligibleForMhtBuyflow, isFemaleSex],
});

export const willReceiveWomensWeightLossModule = (
  { surveyAnswers } = getStore().getState()
) => {
  const { menopauseStatus, ageRange, menstrualCycle, periodEndReason } =
    surveyAnswers;
  if (isEligibleForHRTBuyflow()) {
    return (
      ["30s", "40s"].includes(ageRange?.[0]) &&
      (menstrualCycle?.[0] === "yes" ||
        (menstrualCycle?.[0] === "no" &&
          periodEndReason?.includes("hormonalContraceptive")))
    );
  }
  // Note(James): Any answer to this question will result in the module, so the
  // eligibility is really "anyone who can see the question" - but leaving this segment to be explicit
  return [
    "preMenopause",
    "perimenopause",
    "menopause",
    "postMenopause",
    "preferNotToSay",
  ].includes(menopauseStatus?.[0]);
};

export const willReceiveSomeWomensHealthModule = checkSegments({
  oneOf: [willReceiveWomensWeightLossModule, willReceiveMenstrualCycleModule],
});

export function isAgeRangeEligibleForHRT() {
  const { ageRange, HRTAgeRange40s, ageYears, ageRangeHRT } =
    getSurveyAnswers();

  if (isAppBaseRoute() && ageYears) {
    return isAgeEligibleForMht(ageYears);
  }

  if (isEligibleForStandaloneHRTBuyflow() && ageRangeHRT?.[0]) {
    return ["45-49", "50s"].includes(ageRangeHRT?.[0]);
  }

  return ageRange?.[0] === "50s" || HRTAgeRange40s?.[0] === "yes";
}

export function isLastPeriodEligibleForHRT() {
  const { lastPeriod } = getSurveyAnswers();

  return ["under1Year", "from1To2Years", "from2To10Years"].includes(
    lastPeriod?.[0]
  );
}

export function isPeriodEndReasonEligibleForHRT() {
  const { periodEndReason } = getSurveyAnswers();
  return !periodEndReason?.includes("hormonalContraceptive");
}

export function isMenopauseSymptomsEligibleForHRT() {
  const { menopauseSymptoms } = getSurveyAnswers();
  const validSymptoms = [
    MenopauseSymptoms.HOT_FLASHES,
    MenopauseSymptoms.NIGHT_SWEATS,
    MenopauseSymptoms.SLEEP_DISTURBANCES,
    MenopauseSymptoms.BRAIN_FOG,
    MenopauseSymptoms.CHANGES_IN_MOOD,
    MenopauseSymptoms.JOINT_PAIN,
  ];
  return menopauseSymptoms?.some((symptom) =>
    validSymptoms.includes(symptom as MenopauseSymptoms)
  );
}

export function isMoreThanOneMenopauseSymptomSelected() {
  return (
    getTopSymptomsBasedOnUserSelected(
      Object.keys(MenopauseSymptoms).length,
      true,
      false
    ).length > 1
  );
}

// Note (Anastasiya): it's only reliable if you call it on the checkout page since the slice might be gone after page is refreshed after
export function isStateEligibleForMhtPostCheckoutBuyflow(
  state = getStore().getState()
) {
  const { checkout } = state;
  const { zipcode } = checkout.billingAddress;
  if (zipcode !== undefined) {
    const userState = zipState(zipcode);
    return !appConfig.HRT_INELIGIBLE_US_STATES.includes(userState);
  }
  return false;
}

// Note (Anastasiya): it's only reliable if you call it on the checkout page since the slice might be gone after page is refreshed after
export function isSelectedPlanEligibleForMhtPostCheckoutBuyflow(
  state = getStore().getState()
) {
  const { checkout } = state;
  const isMultiUserPlanPurchased = checkout.multiUserPlan?.toggled;
  return !isMultiUserPlanPurchased;
}

export const isEligibleForMhtPostCheckoutBuyflow = checkSegments({
  allOf: [
    isEligibleForHRTBuyflow,
    isAgeRangeEligibleForHRT,
    isLastPeriodEligibleForHRT,
    isPeriodEndReasonEligibleForHRT,
    isMenopauseSymptomsEligibleForHRT,
    isPregnantStatusEligibleForHRT,
  ],
  noneOf: [hasSignedLiabilityWaiver],
});

export function getSelectedMedPlanType(): MED_SKU {
  if (hasSelectedOralsMetforminPlan()) {
    return MED_SKU.ORALS_METFORMIN;
  }

  if (hasSelectedCompoundedPlan()) {
    return MED_SKU.COMPOUNDED;
  }

  if (hasSelectedHRTCreamPlan()) {
    return MED_SKU.HRT_CREAM;
  }

  if (hasSelectedHRTPatchPlan()) {
    return MED_SKU.HRT_PATCH;
  }

  if (hasSelectedZepboundVialPlan()) {
    return MED_SKU.BRANDED_ZEPBOUND_VIAL;
  }

  if (hasSelectedLiraglutidePlan()) {
    return MED_SKU.GENERIC_LIRAGLUTIDE;
  }

  if (hasSelectedMicrodosePlan()) {
    return MED_SKU.COMPOUNDED_MICRODOSE;
  }

  if (hasSelectedMicrodoseBiomarkerPlan()) {
    return MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER;
  }

  return MED_SKU.BRANDED;
}

export function isSelectedMedPlanWithMedication(sku?: MED_SKU) {
  const selectedMedPlanType = sku || getSelectedMedPlanType();

  return [
    MED_SKU.COMPOUNDED,
    MED_SKU.ORALS_METFORMIN,
    MED_SKU.HRT_CREAM,
    MED_SKU.HRT_PATCH,
    MED_SKU.GENERIC_LIRAGLUTIDE,
    MED_SKU.COMPOUNDED_MICRODOSE,
    MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
  ].includes(selectedMedPlanType);
}

export const isInAppChatLobbyUpsell = () => {
  const urlParams = getURLParams();
  return urlParams?.utm_source === "chatlobby";
};

export const isInAppFitOnUpsell = () => {
  const urlParams = getURLParams();
  return urlParams?.utm_source === "fiton";
};

export function isIdealWeightConfirmationOnFreemiumLPEligible() {
  const { idealWeightKg } = getSurveyAnswers();
  const landingBMI = getLandingPageBMIs({ setIfMissing: true });
  const belowMinimumBMI =
    !!landingBMI && belowMinimumNormalBMI(landingBMI?.targetBMI, true);
  return isUpsellRoute() && !!idealWeightKg && !belowMinimumBMI;
}

export function isFreemiumUpsellDuringOnboarding() {
  const { freemiumOnboardingSourceId } = getSessionState("browser");
  return (
    isFreemiumUpsellRoute() &&
    freemiumOnboardingSourceId === "app-free-onboarding"
  );
}

export function showIdealWeightQuestionOnFreemium() {
  const { surveyAnswers } = getStore().getState();
  const { idealWeightConfirmationFreemium } = surveyAnswers;
  return (
    !idealWeightConfirmationFreemium?.includes("yes") ||
    !isIdealWeightConfirmationOnFreemiumLPEligible()
  );
}

export function shouldShowSurveyHeaderExitButton() {
  return isEligibleForMhtPostCheckoutBuyflow() && !isGlobalExitEnabled();
}

export function isFromMealbot() {
  const { fromMealbot } = getURLParams();
  return fromMealbot === "true";
}

export function isMealPlanAllergyQuestionEnabled() {
  return !!appConfig.MEAL_PLAN_ALLERGY_QUESTION_ENABLED;
}

export function isMealPlanFavoriteCuisineQuestionEnabled() {
  return !!appConfig.MEAL_PLAN_FAVORITE_CUISINE_QUESTION_ENABLED;
}

export function isMealPlanMaxCookingTimeQuestionEnabled() {
  return !!appConfig.MEAL_PLAN_MAX_COOKING_TIME_QUESTION_ENABLED;
}

export const isEligibleForPromoCampaign = checkSegments({
  noneOf: [isClinicalRoute],
});

export function isSideNavNoomMedUpgrade() {
  return (
    isInAppMedUpgrade() &&
    visitTracker.getTrackingParams().utm_source === "sidenav"
  );
}

const metaTrafficSources = ["fb", "ig", "an"];

export function isMetaTraffic() {
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get("utm_source");
  const trafficSource = urlParams.get("traffic_source");
  return (
    metaTrafficSources.includes(utmSource) ||
    metaTrafficSources.includes(trafficSource)
  );
}

export function hasSelectedHRTCreamPlan() {
  return (
    getMedFlagOverride("selectHRTCreamPlan") ??
    getSessionState("browser").selectedHRTPlanType === "cream"
  );
}

export function isFromMealFeedbackScreenWithWeightLossGoal(): boolean {
  // Use current URL parameters, not stored/persisted tracking params
  const urlParams = getURLParams();
  const utmSource = urlParams.utm_source;
  const surveyAnswers = getSurveyAnswers();
  const healthGoalBehavior = surveyAnswers?.healthGoalBehavior;

  // Check if user came from meal feedback screens (current URL only)
  const fromMealFeedback = [
    "meal_feedback_med_upgrade",
    "meal_feedback_weight_upgrade",
    "meal_feedback_content_med_upgrade",
    "meal_feedback_content_weight_upgrade",
    "meal_logging_method_selector_history_med_upgrade",
    "meal_logging_method_selector_history_weight_upgrade",
    "meal_logging_method_selector_barcode_med_upgrade",
    "meal_logging_method_selector_barcode_weight_upgrade",
    "meal_logging_method_selector_search_med_upgrade",
    "meal_logging_method_selector_search_weight_upgrade",
  ].includes(utmSource);

  // Check if user answered "loseWeight" in the healthGoalBehavior question
  // Safety checks: ensure healthGoalBehavior exists, is an array, and contains "loseWeight"
  const userHasWeightLossGoal =
    healthGoalBehavior != null &&
    Array.isArray(healthGoalBehavior) &&
    healthGoalBehavior.includes("loseWeight");

  return fromMealFeedback && userHasWeightLossGoal;
}

export function hasSelectedHRTPatchPlan() {
  return (
    getMedFlagOverride("selectHRTPatchPlan") ??
    getSessionState("browser").selectedHRTPlanType === "patch"
  );
}

// Based on MHT pre-checkout flow
export function isUterusNotRemoved() {
  const { periodEndReason } = getSurveyAnswers();
  return !periodEndReason?.includes("noUterus");
}

export const isNoomVibeBonusCodeRetrievalEligible = checkSegments({
  allOf: [isNoomVibeCodeRetrievalRoute],
});

export const isNoomVibeBonusCodeRetrievalEnabled = () => {
  return localStorage.getItem("vibeMoreRewards") === "startRewards";
};

export const isHRTSurvey = checkSegments({
  allOf: [
    ({ linearBuyflow }) =>
      [
        hrtSurveyLayer,
        hrtPaymentResultsLayer,
        hrtPaymentCheckoutLayer,
      ].includes(getPageSet(linearBuyflow.pageSetName)?.layer),
  ],
});

export function shouldShowIntakeSurveyAfterFreemiumUpsell() {
  return isAndroidPlatform() || (isIOSPlatform() && isAppVersionGTE("13.4.0"));
}

export function isTelehealthHRTIntakeTheme(): boolean {
  const themeString =
    getNoomSessionStorage(SessionStorageKey.telehealthIntakeTheme) ?? null;

  // If session storage doesn't have the stored value search browser URL
  const theme: TelehealthIntakeTheme =
    themeString === null
      ? TelehealthIntakeTheme[getURLParams()?.theme]
      : TelehealthIntakeTheme[themeString];

  return theme === TelehealthIntakeTheme.HRT;
}

export const shouldShowMenopauseBoxOnChoosePlan = checkSegments({
  allOf: [
    isHRTPromoLPTraffic,
    isEligibleForHRTBuyflow,
    isPregnantStatusEligibleForHRT,
    isAgeRangeEligibleForHRT,
  ],
});

export const shouldShowMenopauseBoxOnCheckout = checkSegments({
  allOf: [isHRTPromoLPTraffic, isEligibleForMhtPostCheckoutBuyflow],
});

export function shouldHideBackButton() {
  const urlParams = getURLParams();
  return urlParams.hideBackButton === "true";
}

export function isFlowEligibleForClinicalProducts() {
  return (
    isClinicalRoute() ||
    isInAppMedUpgrade() ||
    isNoomWeightMedBranching() ||
    isHealthRoute()
  );
}

export function isFlowEligibleForHRTProducts() {
  return isHRTSurvey() || isEligibleForStandaloneHRTBuyflow();
}

export function isEligibleForHrtAfterZipcodeQuestion() {
  return (
    getEligibleMedProducts(
      {
        checkBMI: false,
        checkHealthRisks: false,
        checkState: true,
        checkFlow: true,
        requirePreviousMedicationsAnswer: false,
        requireHighestBMIAnswer: false,
      },
      MED_FLOW.HRT
    ).length > 0
  );
}

export function isEligibleForHrtAfterHealthRiskQuestion() {
  return (
    getEligibleMedProducts(
      {
        checkBMI: false,
        checkHealthRisks: true,
        checkState: false,
        checkFlow: true,
        requirePreviousMedicationsAnswer: false,
        requireHighestBMIAnswer: false,
      },
      MED_FLOW.HRT
    ).length > 0
  );
}

const isHRTLink = checkSegments({
  allOf: [() => window.location.pathname.includes("/hrt-")],
});

export const shouldFireMedPixel = checkSegments({
  oneOf: [isWebClinicalRoute, isHRTRoute, isHRTLink],
});

export const isRecommendedForZepboundVialAfterInsuranceCoverageQuestion =
  () => {
    const { surveyAnswers } = getStore().getState();
    const { insuranceCoverage } = surveyAnswers;

    return insuranceCoverage?.[0] === "no";
  };

export const shouldRecommendZepbound = () => {
  const { zepboundMedicationPreference } = getSurveyAnswers();
  return zepboundMedicationPreference?.[0] === "no";
};

export const hasExpressedInterestInNonOralsMedication = () => {
  const { oralsMedicationPreference, oralsPlanPreference } = getSurveyAnswers();

  // If user is in the new experiment, use the new logic
  if (oralsPlanPreference) {
    return (
      oralsPlanPreference === "GLP1" || oralsPlanPreference === "RECOMMENDATION"
    );
  }

  // Fallback to old logic for users not in experiment
  return oralsMedicationPreference?.[0] === "yes";
};

export function isTelex264Active() {
  return !!getMeristemConfigValue("isTelex264Active");
}

export const isFreemiumPageset = (pageSet: PageSet) => {
  return (
    pageSet.id === "USFreemiumUnidentifiedUserMainSurvey" ||
    pageSet.id === "USFreemiumIdentifiedUserMainSurvey"
  );
};

export const isEligibleForMedBuyflow = checkSegments({
  oneOf: [
    isEligibleForClinicalBuyflow,
    isEligibleForStandaloneHRTBuyflow,
    isEligibleForHealthBuyflow,
  ],
});

export function isZepboundCashPay() {
  const { userData } = getStore().getState();
  return userData.indicatedZepboundVialPreference;
}

export function hasIndicatedZepboundVialPreference() {
  return getSelectedMedPlanType() === MED_SKU.BRANDED_ZEPBOUND_VIAL;
}

export function isZepboundFeatureEnabled() {
  const params = new URLSearchParams(window.location.search);
  return params.get("feature") === "zepbound";
}

export const isEligibleForDiabetesSupport = (state = getStore().getState()) => {
  const diabetesDiagnosis = state?.surveyAnswers?.diabetesDiagnosis?.[0];
  const diabetesType = state?.surveyAnswers?.diabetesType?.[0];
  return (
    diabetesDiagnosis === "yes" &&
    (diabetesType === "type2" || diabetesType === "unsure")
  );
};

export function hasSignedLiabilityWaiver(state = getStore().getState()) {
  const surveyAnswers = getSurveyAnswers(state);
  return surveyAnswers.liabilityWaiver?.[0] === "agree";
}

export function areLiraglutideMedPlansEnabled() {
  return getLiraglutideRollout().shouldEnableFeature();
}

export function isMicrodosePaidTraffic() {
  return getURLParams()?.feature === "microdose" || isCalorieClinicalRoute();
}

export function isTelex242BVariation() {
  return !!getMeristemConfigValue("isTelex242BVar");
}

export function checkPDQuestionsEligibility() {
  const {
    pdLifting,
    pdGastrointestinalDiscomfort,
    pdGastrointestinalIssues,
    pdSideEffectConcern,
    pdNauseaImpact,
  } = getSurveyAnswers();

  if (
    ["none", "01"].includes(pdLifting?.[0]) ||
    ["01_to_03", "04_to_09", "10+"].includes(
      pdGastrointestinalDiscomfort?.[0]
    ) ||
    (pdGastrointestinalIssues && !pdGastrointestinalIssues.includes("none")) ||
    ["somewhatConcerned", "veryConcerned"].includes(pdSideEffectConcern?.[0]) ||
    ["yes", "notSure"].includes(pdNauseaImpact?.[0])
  ) {
    return true;
  }
  return false;
}

export enum PDExceptionReason {
  AGE_50_PLUS = "age50Plus",
  CONTINUITY_OF_CARE = "continuityOfCare",
}

export function getPersonalizedDosingEligibility(): {
  isEligible: boolean;
  reason?: PDExceptionReason;
} {
  const questionEligibility = checkPDQuestionsEligibility();

  if (questionEligibility) {
    return { isEligible: true };
  }

  if (isAge50Plus()) {
    return {
      isEligible: true,
      reason: PDExceptionReason.AGE_50_PLUS,
    };
  }

  const { currentSubscriptionData } = getStore().getState();
  if (
    isContinuityOfCareEnabled() &&
    currentSubscriptionData.continuityOfCareEligible
  ) {
    return {
      isEligible: true,
      reason: PDExceptionReason.CONTINUITY_OF_CARE,
    };
  }

  return { isEligible: false };
}

export const isQualifiedForPersonalizedDosing = () => {
  return getPersonalizedDosingEligibility().isEligible;
};

export function isAge50Plus() {
  const { ageRange, ageYears } = getSurveyAnswers();
  const ageParsed = ageRange?.[0] ?? transformAgeYearsToAgeRange(ageYears);

  return ["50s", "60s", "70s"].includes(ageParsed);
}

export function hasWeightLossGoal(state = getStore().getState()) {
  const { bodyCompositionGoal } = getSurveyAnswers(state);
  return bodyCompositionGoal?.[0] === "loseWeight";
}

export function isAffiliateTrial30PercentOff() {
  return (
    isAffiliateWithTrial() &&
    isUS() &&
    isEnLanguage() &&
    visitTracker.getTrackingParams().utm_content === "1974100"
  );
}

export const shouldSendWeightLossGoalData = checkSegments({
  noneOf: [isAppFreeOnboardingRoute],
});

export const isCountryEligibleForAppFreeOnboarding = checkSegments({
  oneOf: [isUS, isCA, isAU],
});

export const isEligibleForAppFreeOnboarding = checkSegments({
  allOf: [
    isAppFreeOnboardingRoute,
    isEnLanguage,
    isCountryEligibleForAppFreeOnboarding,
  ],
});

export const isEligibleForFutureMe = () => {
  const { ageYears } = getSurveyAnswers();
  // const isAppVersionGTE13280 = isAppVersionGTE("13.28.0");
  const isAgeValid = !!ageYears && ageYears >= 18 && ageYears <= 70;
  // return isAppVersionGTE13280 && isAgeValid;
  return isAgeValid;
};

export const isFutureMeSupported = () =>
  getURLParams()?.is_future_me_supported === "true" &&
  !!appConfig.FUTURE_ME_ENABLED;

export const isFriendsPromotionSupported = () =>
  getURLParams()?.is_friends_supported === "true";

export const isAtRiskSubscriberReOnboarding = checkSegments({
  allOf: [
    isEnLanguage,
    () => getURLParams()?.utm_medium === "app",
    () => getURLParams()?.utm_campaign === "ex1120",
  ],
});

export function isLongevityMicrodoseEnabled() {
  return getLongevityMicrodoseRollout().shouldEnableFeature();
}

export function isCompoundedMicrodoseProactiveHealthBiomarkerEligible() {
  return getEligibleMedProducts().includes(
    MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER
  );
}

/**
 * Determines if user should see Proactive Health phase 2 flow.
 * This function restores the old behavior of isCompoundedMicrodoseProactiveHealthBiomarkerEligible().
 * Previously, PHMDS was only eligible in low BMI window (21-25, or 21-27 for FL/NJ), where full-dose GLP-1
 * products were not eligible. This function returns true only in those same cases.
 * Uses useLegacyPHMDSRules option to get eligible products with old BMI logic.
 */
export function shouldShowProactiveHealthFlow() {
  const eligibleProducts = getEligibleMedProducts({
    checkBMI: true,
    checkHealthRisks: true,
    checkState: true,
    checkFlow: true,
    requirePreviousMedicationsAnswer: false,
    requireHighestBMIAnswer: false,
    useLegacyPHMDSRules: true,
  });

  return eligibleProducts.includes(
    MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER
  );
}

export function isEligibleForA1cLabsInterstitial() {
  const isProactiveHealthEligible =
    isCompoundedMicrodoseProactiveHealthBiomarkerEligible();
  const isGlp1Eligible = isQualifiedForFullDoseGLP1ForHealth();
  const noWeightLossGoal = !hasWeightLossGoal();

  return isProactiveHealthEligible || (isGlp1Eligible && noWeightLossGoal);
}

export function shouldSkipPreviousMedicationsQuestion() {
  const products = getEligibleMedProducts({
    checkBMI: true,
    checkHealthRisks: false,
    checkState: false,
    checkFlow: true,
    requirePreviousMedicationsAnswer: false,
    requireHighestBMIAnswer: true,
    disabledNamedChecks: [
      "isQualifiedForPD",
      "isNotEligibleForCompoundedSemaglutide",
    ],
  });

  return (
    products.length === 1 &&
    products[0] === MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER
  );
}

export function shouldShowMedicationPreference() {
  const eligibleProducts = getEligibleMedProducts();

  return (
    eligibleProducts.includes(MED_SKU.COMPOUNDED) &&
    eligibleProducts.includes(MED_SKU.COMPOUNDED_MICRODOSE)
  );
}

export const isEligibleForMedicationPreference = () => {
  return shouldShowMedicationPreference() && isMicrodosePaidTraffic();
};

export const shouldRecommendCompoundeMicrodosePlan = () => {
  const { eligibleMedicationPreference } = getSurveyAnswers();

  if (!isEligibleForMedicationPreference() || !eligibleMedicationPreference) {
    return true;
  }

  // For CalAI users who selected "Give me a recommendation", default to microdose
  if (
    eligibleMedicationPreference === "RECOMMENDATION" &&
    isCalorieClinicalRoute()
  ) {
    return true;
  }

  return eligibleMedicationPreference === "COMPOUNDED_MICRODOSE";
};

export const isOpenToMedRecommendation = () => {
  const {
    eligibleMedicationPreference,
    oralsMedicationPreference,
    oralsPlanPreference,
  } = getSurveyAnswers();

  return Boolean(
    !shouldRecommendZepbound() &&
      oralsMedicationPreference?.[0] !== "no" &&
      (!eligibleMedicationPreference ||
        eligibleMedicationPreference === "RECOMMENDATION") &&
      (!oralsPlanPreference || oralsPlanPreference === "RECOMMENDATION")
  );
};

export function shouldUseOffersRecommendation() {
  return getMeristemConfigValue("shouldUseOffersRecommendation") === "true";
}

export function getForcedMedRecommendationSku() {
  const value = getMeristemConfigValue("forcedMedRecommendation");
  return Object.values(MED_SKU).includes(value as MED_SKU)
    ? (value as MED_SKU)
    : null;
}

export const shouldCallRecommenderOffersApi = () => {
  return isClinicalRoute();
};

export const shouldShowNoTrialMedCheckout = () => {
  return (
    getSelectedMedPlanType() ===
    MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER
  );
};

export const isTelex265BActive = () => {
  return !!getMeristemConfigValue("isTelex265BActive");
};

export const isTikTokSource = () => {
  const urlParams = getURLParams();
  return urlParams?.utm_source === "tiktok";
};

export const showSeedsEducationScreen = () => {
  return isAppFreeOnboardingScreenVisible("seedsEducation");
};

export const showRealWorldRewardsScreen = () => {
  return isAppFreeOnboardingScreenVisible("realWorldRewards");
};

export const showFaceScanIntroScreen = () => {
  return isAppFreeOnboardingScreenVisible("faceScanIntro");
};

export const showAddProfilePhotoScreen = () => {
  return isAppFreeOnboardingScreenVisible("addProfilePhoto");
};

function isAppFreeOnboardingScreenVisible(pageName: string): boolean {
  const urlParams = getURLParams();
  const visibilityConfig = urlParams?.v_conf?.split(",") ?? [];
  return visibilityConfig.includes(pageName);
}

export const shouldUseStripePaymentElement = () => {
  return (
    getMeristemConfigValue("shouldUseStripePaymentElementTelex269") === "true"
  );
};

export const isEligibleForWalletPayment = () => {
  const { recommendedPlan } = getStore().getState();
  return (
    (recommendedPlan.payment_source === "STRIPE" ||
      isOrdersPurchaseCheckout() ||
      isEligibleForOrdersMedCheckout()) &&
    !isInApp()
  );
};

export const shouldUseBraintreeCreditCard = () => {
  const { recommendedPlan } = getStore().getState();
  return (
    recommendedPlan.payment_source === "BRAINTREE" &&
    !isOrdersPurchaseCheckout()
  );
};

export const show72MoreWeightDisclaimer = () => {
  const urlParams = getURLParams();
  return urlParams?.utm_content === "moreweightloss_popup";
};

export const shouldHidePartnerAd = () => {
  if (isCalorieClinicalRoute() && isInsidePartnerWebView()) {
    return true;
  }

  return false;
};

export function isContinuityOfCareEnabled() {
  return (
    getMedFlagOverride("continuityOfCareEnabledOverwrite") ??
    !!appConfig.CONTINUITY_OF_CARE_ENABLED
  );
}

export const getMedPlanChangeTermsExpVariant = () =>
  getMeristemConfigValue("autoRenewalTermsPosition");

export const shouldUseStickyProgressBar = () => {
  return (
    getMeristemConfigValue("isStickyProgressBarExperimentEnabled") === "true"
  );
};

export function isTelex274Active() {
  return !!getMeristemConfigValue("isTelex274Active");
}

export function isEligibleForOrdersMedCheckout() {
  return (
    isEligibleForClinicalBuyflow() ||
    isEligibleForStandaloneHRTBuyflow() ||
    isEligibleForHealthBuyflow()
  );
}

export function isTelex276Active() {
  return !!getMeristemConfigValue("isTelex276Active");
}

export const isOnboardingHealthReport = () =>
  getURLParams()?.ad_location === UpsellAdLocation.OnboardingHealthReport;

export function isQualifiedForFullDoseGLP1ForHealth() {
  const products = getEligibleMedProducts({
    checkBMI: true,
    checkHealthRisks: true,
    checkState: false,
    checkFlow: true,
    requirePreviousMedicationsAnswer: false,
    requireHighestBMIAnswer: false,
    disabledNamedChecks: ["isQualifiedForPD"],
  });

  return isFulldoseGlp1Eligible(products);
}

export function isFacialBloodScanAppOnboardingEnabled() {
  return !!appConfig.FACIAL_BLOOD_SCAN_APP_ONBOARDING_ENABLED;
}

export function isEligibleForWeightGraphInMedCheckout() {
  if (acceptedNoomClinicalOffer() && hasSelectedMicrodoseBiomarkerPlan()) {
    return false;
  }

  if (isHealthRoute() && !hasWeightLossGoal()) {
    return false;
  }

  // Fallback in case somehow a user doesn't have an ideal weight
  if (!hasIdealWeight()) {
    return false;
  }

  return true;
}

export const hasHealthReportUpsellContent = (
  { upsellAdData } = getStore().getState()
) => !!(upsellAdData?.healthReportUpsell?.content as HealthReportUpsellContent);

export function isOnAppleExternalLinkoutPath() {
  return window.location.pathname.startsWith("/payment/linkout/");
}

export function isApplePurchaseLinkoutEnabled(state = getStore().getState()) {
  if (isOnAppleExternalLinkoutPath()) {
    return true;
  }

  if (
    (!isFreemiumWeightRoute(state) && !isFreemiumBranchedRoute(state)) ||
    !getMeristemConfigValue("APPLE_PURCHASE_LINKOUT_ENABLED")
  ) {
    return false;
  }

  return (
    isDevMode() ||
    (isIOSPlatform() &&
      isAppVersionGTE("13.47.0") &&
      !!getIOSMessageHandlers()?.openExternalBrowser)
  );
}

export const shouldShowUpdateMinBMIModalForHealthRoute = () => {
  return (
    isHealthRoute() &&
    (isQualifiedForFullDoseGLP1ForHealth() ||
      isCompoundedMicrodoseProactiveHealthBiomarkerEligible())
  );
};
